import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  Shield,
  RefreshCw,
  Heart,
  FileText,
  Download,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  Users,
  User,
  Activity,
  Loader2,
  Star,
  MessageCircle,
  Send,
  Sparkles,
  Bot,
  Building2,
  Ban,
  Phone,
  IndianRupee,
  Zap,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Percent,
  Calendar,
  Stethoscope,
  Ambulance,
  Pill,
  Baby,
  Eye,
  BookOpen,
  Link2,
  Home,
  Calculator,
  Headphones,
  Upload,
  Smartphone,
  Play,
  Video,
  Layers,
  TrendingUp,
  DollarSign,
  Award,
  Target,
  Timer,
  Briefcase,
  UserCheck,
  Scale,
  BarChart3,
  PieChart,
  Check
} from "lucide-react";
import axios from "axios";
import { trackPlanView, trackDocumentDownload, trackAskAI } from "@/utils/analytics";
import PAPlanDetail from "@/pages/PAPlanDetail";
import HealthPlanDetail from "@/pages/HealthPlanDetail";
import SectionNav from "@/components/SectionNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tab configuration for HEALTH insurance
const HEALTH_TABS = [
  { id: "overview", label: "Overview", icon: Star, color: "from-[#D4A017] to-yellow-500" },
  { id: "claim-experience", label: "Claim Experience", icon: Activity, color: "from-emerald-500 to-teal-500" },
  { id: "video", label: "Product Video", icon: Play, color: "from-red-500 to-pink-500", requiresVideo: true },
  { id: "maternity", label: "Maternity Benefits", icon: Baby, color: "from-pink-500 to-rose-400", requiresMaternity: true },
  { id: "benefits", label: "Benefits", icon: Shield, color: "from-emerald-500 to-green-500" },
  { id: "coverage", label: "Coverage", icon: Activity, color: "from-[#EDF1F8] to-cyan-500" },
  { id: "waiting", label: "Waiting Periods", icon: Clock, color: "from-[#EDF1F8] to-[#DCE4F2]" },
  { id: "exclusions", label: "Exclusions", icon: XCircle, color: "from-red-500 to-rose-500" },
  { id: "riders", label: "Riders & Add-ons", icon: Heart, color: "from-pink-500 to-rose-400" },
  { id: "discounts", label: "Discounts", icon: Percent, color: "from-green-500 to-emerald-500" },
  { id: "network", label: "Hospitals", icon: Building2, color: "from-cyan-500 to-[#DCE4F2]" },
  { id: "documents", label: "Documents", icon: FileText, color: "from-gray-500 to-slate-500" },
  { id: "askme", label: "Ask AI", icon: Sparkles, color: "from-[#EDF1F8] to-[#DCE4F2]" }
];

// Tab configuration for TERM insurance - focused on what matters for term plan decision
const TERM_TABS = [
  { id: "overview", label: "Overview", icon: Star, color: "from-[#D4A017] to-yellow-500" },
  { id: "claim-experience", label: "Claim Settlement", icon: Activity, color: "from-emerald-500 to-teal-500" },
  { id: "death-benefit", label: "Death Benefit", icon: Shield, color: "from-[#EDF1F8] to-cyan-500" },
  { id: "plan-options", label: "Plan Options", icon: Layers, color: "from-purple-500 to-violet-500" },
  { id: "benefits", label: "Key Features", icon: CheckCircle2, color: "from-emerald-500 to-green-500" },
  { id: "riders", label: "Riders & Add-ons", icon: Heart, color: "from-pink-500 to-rose-400" },
  { id: "waiting", label: "Waiting Period", icon: Clock, color: "from-[#EDF1F8] to-[#DCE4F2]" },
  { id: "exclusions", label: "Exclusions", icon: XCircle, color: "from-red-500 to-rose-500" },
  { id: "surrender", label: "Surrender & Maturity", icon: TrendingUp, color: "from-[#D4A017] to-[#D9A82C]" },
  { id: "documents", label: "Documents", icon: FileText, color: "from-gray-500 to-slate-500" },
  { id: "askme", label: "Ask AI", icon: Sparkles, color: "from-[#EDF1F8] to-[#DCE4F2]" }
];

// Legacy BASE_TABS for backward compatibility
const BASE_TABS = HEALTH_TABS;

// Expandable Section Component
const ExpandableSection = ({ title, icon: Icon, iconColor, children, defaultOpen = false, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="glass-card overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#EDF1F8]/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${iconColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className="h-5 w-5 text-[#1B2A4A]" />
          </div>
          <h3 className="font-bold text-[#1B2A4A] text-lg">{title}</h3>
          {badge && (
            <span className="px-2 py-1 text-xs font-medium bg-[#D4A017]/15 text-[#A87608] rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#3A4557]" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#3A4557]" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-5 pb-5 border-t border-[#C9D4E6] pt-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Detail Row Component for tables
const DetailRow = ({ label, value, highlight = false, icon: Icon }) => (
  <div className={`flex items-start justify-between py-3 border-b border-blue-500/10 last:border-0 ${highlight ? 'bg-[#D4A017]/5 -mx-4 px-4' : ''}`}>
    <div className="flex items-center gap-2 text-[#3A4557]">
      {Icon && <Icon className="h-4 w-4 text-[#3A4557]" />}
      <span className="font-medium">{label}</span>
    </div>
    <div className="text-[#1B2A4A] font-medium text-right max-w-[60%]">{value || "N/A"}</div>
  </div>
);

// Helper to render any value properly
const renderValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? renderValue(v) : v}`)
      .join(" | ");
  }
  return String(value);
};

// Get rider icon based on name
const getRiderIcon = (riderName) => {
  const name = riderName.toLowerCase();
  if (name.includes('maternity') || name.includes('parenthood')) return Baby;
  if (name.includes('hospital cash')) return IndianRupee;
  if (name.includes('critical')) return Heart;
  if (name.includes('opd') || name.includes('wellbeing')) return Stethoscope;
  if (name.includes('chronic') || name.includes('abcd')) return Pill;
  if (name.includes('restore') || name.includes('reset') || name.includes('unlimited')) return RefreshCw;
  if (name.includes('air') || name.includes('ambulance')) return Ambulance;
  if (name.includes('e-opinion') || name.includes('second')) return Eye;
  return Shield;
};

export default function PlanDetail() {
  const { planId, section = "overview" } = useParams();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(section);
  const [expandedRiders, setExpandedRiders] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Determine if this is a term insurance plan
  const isTermPlan = plan?.type === "term";
  
  // Select appropriate tabs based on plan type
  const baseTabs = isTermPlan ? TERM_TABS : HEALTH_TABS;
  
  // Filter tabs based on plan data
  const TABS = baseTabs.filter(tab => {
    if (tab.requiresVideo) {
      return plan?.marketing_video?.url;
    }
    if (tab.requiresMaternity) {
      return plan?.maternity_coverage || plan?.maternity_benefits || plan?.id === 'star-women-care';
    }
    return true;
  });
  
  // ASK ME
  const [askQuestion, setAskQuestion] = useState("");
  const [askHistory, setAskHistory] = useState([]);
  const [askLoading, setAskLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  
  // Document Library documents
  const [libraryDocuments, setLibraryDocuments] = useState([]);

  useEffect(() => { fetchPlanData(); fetchLibraryDocuments(); }, [planId]);
  useEffect(() => { setActiveTab(section); }, [section]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [askHistory]);

  const fetchPlanData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/plans/${planId}`);
      setPlan(response.data);
      // Track plan view in Google Analytics
      trackPlanView(planId, response.data.name, response.data.company);
    } catch (error) {
      toast.error("Failed to load plan");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchLibraryDocuments = async () => {
    try {
      const response = await axios.get(`${API}/plans/${planId}/documents`);
      setLibraryDocuments(response.data.documents || []);
    } catch (error) {
      console.log("No library documents found for this plan");
      setLibraryDocuments([]);
    }
  };

  const handleAskQuestion = async (question = askQuestion) => {
    if (!question.trim()) return;
    setAskHistory(prev => [...prev, { type: "user", text: question }]);
    setAskQuestion("");
    setAskLoading(true);
    // Track Ask AI usage
    trackAskAI(plan?.name || planId, question);
    try {
      const response = await axios.post(`${API}/plans/${planId}/ask`, { plan_id: planId, question, session_id: sessionId });
      if (!sessionId) setSessionId(response.data.session_id);
      setAskHistory(prev => [...prev, { type: "ai", text: response.data.answer }]);
    } catch (error) {
      setAskHistory(prev => [...prev, { type: "error", text: "Sorry, please try again." }]);
    } finally {
      setAskLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/plan/${planId}/${tabId}`);
  };

  const toggleRiderExpand = (riderId) => {
    setExpandedRiders(prev => ({ ...prev, [riderId]: !prev[riderId] }));
  };

  // Find rider document
  const findRiderDocument = (riderId, riderName) => {
    if (!plan?.rider_documents) return null;
    return plan.rider_documents.find(doc => 
      doc.rider === riderId || 
      doc.name.toLowerCase().includes(riderName.toLowerCase().split(' ')[0])
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#A87608] animate-spin mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading plan details...</p>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  if (plan.type === "pa") return <PAPlanDetail plan={plan} />;

  if (plan.type === "health" && plan.expert_schema) return <HealthPlanDetail plan={plan} />;

  return (
    <div className="min-h-screen pb-12">
      {/* Header */}
      <header className="glass-nav sticky top-2 mx-2 sm:mx-6 z-50 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between rounded-xl sm:rounded-2xl">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")} 
            className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3"
            title="Home"
          >
            <Home className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="text-[#2E3B57] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40 px-2 sm:px-3"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Back</span>
          </Button>
          <div className="hidden md:flex items-center gap-3">
            <img src={plan.company_logo} alt={plan.company} className="h-8 w-8 object-contain bg-white rounded-lg p-1" />
            <span className="font-semibold text-[#1B2A4A] text-sm">{plan.name}</span>
          </div>
        </div>
        <a href="tel:+919510020907">
          <Button className="btn-gold h-8 sm:h-10 px-3 sm:px-5 text-xs sm:text-sm">
            <Phone className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">Get Quote</span>
          </Button>
        </a>
      </header>

      {/* Hero */}
      <section className="pt-8 pb-6 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <img src={plan.company_logo} alt={plan.company} className="h-16 w-16 object-contain mx-auto mb-4 bg-white rounded-xl p-2" />
          <p className="text-[#A87608] font-semibold mb-2">{plan.company}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1B2A4A] mb-4">{plan.name}</h1>
          <p className="text-lg text-[#2E3B57] max-w-3xl mx-auto">{plan.description}</p>
          
          {plan.usp && plan.usp.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {plan.usp.slice(0, 6).map((usp, idx) => (
                <span key={idx} className="badge-green">
                  <CheckCircle2 className="h-4 w-4" />
                  {usp}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Tabs - Grid on mobile, horizontal scroll on desktop */}
      <div className="sticky top-14 sm:top-20 z-40 px-2 sm:px-6 pb-4">
        <div className="max-w-6xl mx-auto">
          {/* Mobile: scrollable chips + All Sections sheet */}
          <div className="sm:hidden bg-white/95 backdrop-blur-md rounded-xl shadow-sm border border-[#C9D4E6] p-2">
            <SectionNav tabs={TABS} activeTab={activeTab} onChange={handleTabChange} testIdPrefix="tab" />
          </div>
          
          {/* Desktop: Multi-row grid layout */}
          <div className="hidden sm:block glass-card p-3">
            <div className="grid grid-cols-4 lg:grid-cols-6 gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.color} text-[#1B2A4A] shadow-lg`
                      : 'text-[#3A4557] hover:text-[#1B2A4A] hover:bg-[#EDF1F8]/40'
                  }`}
                >
                  <tab.icon className="h-4 w-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* OVERVIEW */}
            {activeTab === "overview" && (
              <div className="space-y-4">
                {/* Quick Summary Card */}
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-4 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-yellow-500 flex items-center justify-center">
                      <Star className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Plan at a Glance
                  </h2>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                      <p className="text-[#3A4557] text-sm mb-1">Sum Insured Options</p>
                      <p className="text-[#1B2A4A] font-bold">{plan.sum_insured_options?.length || 0} options</p>
                      <p className="text-[#A87608] text-sm mt-1">
                        ₹{plan.sum_insured_options?.[0]} - ₹{plan.sum_insured_options?.[plan.sum_insured_options.length - 1]}
                      </p>
                    </div>
                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                      <p className="text-[#3A4557] text-sm mb-1">Policy Type</p>
                      <p className="text-[#1B2A4A] font-bold">{plan.policy_options?.types?.join(", ") || "Individual/Family"}</p>
                    </div>
                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                      <p className="text-[#3A4557] text-sm mb-1">Entry Age</p>
                      <p className="text-[#1B2A4A] font-bold">{plan.policy_options?.entry_age || "18-65 years"}</p>
                    </div>
                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                      <p className="text-[#3A4557] text-sm mb-1">Renewal</p>
                      <p className="text-[#1B2A4A] font-bold">{plan.policy_options?.renewal || "Lifelong"}</p>
                    </div>
                  </div>
                </div>

                {/* Plan Variants - Clean & Efficient */}
                {plan.variants && plan.variants.length > 0 && (
                  <div className="glass-card p-6 mb-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                        <Shield className="h-5 w-5 text-[#1B2A4A]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#1B2A4A] text-lg">
                          {plan.variants.length === 1 ? 'Plan Variant' : 'Choose Your Variant'}
                        </h3>
                        <p className="text-[#3A4557] text-sm">
                          {plan.variants.length === 1 ? 'Single variant available' : `${plan.variants.length} options to choose from`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Variant List */}
                    <div className="space-y-3">
                      {plan.variants.map((variant, idx) => (
                        <div
                          key={variant.id || idx}
                          className={`rounded-xl border-2 transition-all overflow-hidden ${
                            selectedVariant === idx 
                              ? 'border-[#D4A017] bg-[#D4A017]/5' 
                              : 'border-[#C9D4E6] bg-[#EDF1F8]/30 hover:border-[#C9D4E6]'
                          }`}
                        >
                          {/* Clickable Header */}
                          <button
                            onClick={() => setSelectedVariant(selectedVariant === idx ? null : idx)}
                            className="w-full text-left p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                selectedVariant === idx 
                                  ? 'bg-[#D4A017] text-black' 
                                  : 'bg-[#E2E9F4] text-[#1B2A4A]'
                              }`}>
                                {variant.coverage_multiplier ? (
                                  <span className="font-bold text-sm">{variant.coverage_multiplier}</span>
                                ) : (
                                  <Shield className="h-5 w-5" />
                                )}
                              </div>
                              <div>
                                <h4 className={`font-bold ${selectedVariant === idx ? 'text-[#A87608]' : 'text-[#1B2A4A]'}`}>
                                  {variant.name}
                                  {variant.is_new && (
                                    <span className="ml-2 text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold align-middle">NEW</span>
                                  )}
                                </h4>
                                <p className="text-sm text-[#3A4557] line-clamp-1">
                                  {variant.key_feature || variant.description}
                                </p>
                              </div>
                            </div>
                            <ChevronDown className={`h-5 w-5 text-[#3A4557] transition-transform ${
                              selectedVariant === idx ? 'rotate-180 text-[#A87608]' : ''
                            }`} />
                          </button>

                          {/* Expanded Content */}
                          <AnimatePresence>
                            {selectedVariant === idx && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                              >
                                <div className="px-4 pb-4 space-y-4">
                                  {/* Key Feature Box */}
                                  <div className="p-4 bg-[#D4A017]/10 rounded-lg border border-[#D4A017]/20">
                                    <p className="text-sm text-[#A87608] font-medium mb-1">Key Feature</p>
                                    <p className="text-[#1B2A4A] font-medium">
                                      {variant.key_feature || variant.description}
                                    </p>
                                  </div>

                                  {/* Description if different from key_feature */}
                                  {variant.description && variant.description !== variant.key_feature && (
                                    <p className="text-[#2E3B57] text-sm">{variant.description}</p>
                                  )}

                                  {/* Highlights for new variants */}
                                  {variant.highlights && variant.highlights.length > 0 && (
                                    <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 space-y-1.5">
                                      <p className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">Highlights</p>
                                      {variant.highlights.map((h, hi) => (
                                        <div key={hi} className="flex items-start gap-2">
                                          <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                                          <p className="text-sm text-slate-800">{h}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Quick Stats Row */}
                                  <div className="flex flex-wrap gap-3">
                                    {variant.coverage_multiplier && (
                                      <div className="px-3 py-2 bg-[#EDF1F8]/30 rounded-lg border border-[#C9D4E6]">
                                        <span className="text-xs text-[#3A4557]">Coverage: </span>
                                        <span className="text-[#2E3B57] font-bold">{variant.coverage_multiplier}</span>
                                      </div>
                                    )}
                                    {plan.waiting_periods?.initial && (
                                      <div className="px-3 py-2 bg-[#E2E9F4]/50 rounded-lg">
                                        <span className="text-xs text-[#3A4557]">Initial Wait: </span>
                                        <span className="text-[#1B2A4A] font-medium">
                                          {typeof plan.waiting_periods.initial === 'object' 
                                            ? plan.waiting_periods.initial.period 
                                            : plan.waiting_periods.initial}
                                        </span>
                                      </div>
                                    )}
                                    {plan.waiting_periods?.pre_existing && (
                                      <div className="px-3 py-2 bg-[#E2E9F4]/50 rounded-lg">
                                        <span className="text-xs text-[#3A4557]">PED Wait: </span>
                                        <span className="text-[#1B2A4A] font-medium">
                                          {typeof plan.waiting_periods.pre_existing === 'object' 
                                            ? plan.waiting_periods.pre_existing.period 
                                            : plan.waiting_periods.pre_existing}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Get Quote Button */}
                                  <Button 
                                    className="w-full bg-[#D4A017] hover:bg-[#D4A017] text-black font-bold"
                                    onClick={() => window.open(`https://wa.me/919004054057?text=Hi! I'm interested in ${plan.name} - ${variant.name}. Please share details.`, '_blank')}
                                  >
                                    <Phone className="h-4 w-4 mr-2" />
                                    Get Quote for {variant.name}
                                  </Button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sum Insured Options */}
                {plan.sum_insured_options && plan.sum_insured_options.length > 0 && (
                  <ExpandableSection 
                    title="Sum Insured Options" 
                    icon={IndianRupee} 
                    iconColor="from-emerald-500 to-green-500"
                    defaultOpen={true}
                  >
                    <p className="text-[#3A4557] mb-4">Available coverage amounts to choose from</p>
                    <div className="flex flex-wrap gap-3">
                      {plan.sum_insured_options.map((si, idx) => (
                        <div key={idx} className="px-5 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                          <span className="font-bold text-[#1B2A4A] text-lg">₹{si}</span>
                        </div>
                      ))}
                    </div>
                  </ExpandableSection>
                )}

                {/* Policy Options */}
                {plan.policy_options && (
                  <ExpandableSection 
                    title="Eligibility & Policy Options" 
                    icon={Users} 
                    iconColor="from-[#EDF1F8] to-[#DCE4F2]"
                    defaultOpen={true}
                  >
                    <div className="space-y-3">
                      {plan.policy_options.types && (
                        <DetailRow 
                          label="Policy Types Available" 
                          value={plan.policy_options.types.join(", ")}
                          icon={Users}
                        />
                      )}
                      {plan.policy_options.entry_age && (
                        <DetailRow 
                          label="Entry Age" 
                          value={plan.policy_options.entry_age}
                          icon={Calendar}
                        />
                      )}
                      {plan.policy_options.renewal && (
                        <DetailRow 
                          label="Renewal Age" 
                          value={plan.policy_options.renewal}
                          icon={RefreshCw}
                        />
                      )}
                      {plan.policy_options.max_members_floater && (
                        <DetailRow 
                          label="Max Members (Floater)" 
                          value={plan.policy_options.max_members_floater}
                          icon={Users}
                        />
                      )}
                      {plan.policy_options.tenure && (
                        <DetailRow 
                          label="Policy Tenure Options" 
                          value={Array.isArray(plan.policy_options.tenure) ? plan.policy_options.tenure.join(", ") : plan.policy_options.tenure}
                          icon={Calendar}
                        />
                      )}
                      {plan.policy_options.payment_modes && (
                        <DetailRow 
                          label="Payment Modes" 
                          value={Array.isArray(plan.policy_options.payment_modes) ? plan.policy_options.payment_modes.join(", ") : plan.policy_options.payment_modes}
                          icon={IndianRupee}
                        />
                      )}
                      {/* Term-specific options */}
                      {plan.policy_options.maturity_age && (
                        <DetailRow 
                          label="Maximum Maturity Age" 
                          value={plan.policy_options.maturity_age}
                          icon={Calendar}
                        />
                      )}
                      {plan.policy_options.policy_term && (
                        <DetailRow 
                          label="Policy Term" 
                          value={plan.policy_options.policy_term}
                          icon={Clock}
                        />
                      )}
                      {plan.policy_options.premium_payment && (
                        <DetailRow 
                          label="Premium Payment Options" 
                          value={plan.policy_options.premium_payment}
                          icon={IndianRupee}
                        />
                      )}
                      {plan.policy_options.premium_frequency && (
                        <DetailRow 
                          label="Premium Frequency" 
                          value={plan.policy_options.premium_frequency}
                          icon={Calendar}
                        />
                      )}
                    </div>
                  </ExpandableSection>
                )}

                {/* IRDAI Claim Experience - Term Insurance */}
                {plan.type === 'term' && plan.claim_experience && (
                  <ExpandableSection 
                    title="IRDAI Claim Experience (FY 2023-24)" 
                    icon={Activity} 
                    iconColor="from-[#D4A017] to-yellow-500"
                    badge="Official Data"
                    defaultOpen={true}
                  >
                    <p className="text-[#3A4557] mb-4">Official claim settlement data from IRDAI Annual Report</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {plan.claim_experience.claim_settlement_ratio && (
                        <div className="bg-emerald-500/10 rounded-xl p-5 border border-emerald-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            <p className="text-[#3A4557] text-sm">Claim Settlement Ratio</p>
                          </div>
                          <p className="text-3xl font-bold text-emerald-600">{plan.claim_experience.claim_settlement_ratio}</p>
                          <p className="text-xs text-[#3A4557] mt-1">Individual Death Claims</p>
                        </div>
                      )}
                      {plan.claim_experience.average_claim_time && (
                        <div className="bg-[#EDF1F8]/30 rounded-xl p-5 border border-blue-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-5 w-5 text-[#2E3B57]" />
                            <p className="text-[#3A4557] text-sm">Average Settlement Time</p>
                          </div>
                          <p className="text-2xl font-bold text-[#2E3B57]">{plan.claim_experience.average_claim_time}</p>
                          <p className="text-xs text-[#3A4557] mt-1">From claim registration</p>
                        </div>
                      )}
                      {plan.claim_experience.company_rating && (
                        <div className="bg-[#D4A017]/10 rounded-xl p-5 border border-[#D4A017]/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Star className="h-5 w-5 text-[#A87608]" />
                            <p className="text-[#3A4557] text-sm">Company Rating</p>
                          </div>
                          <p className="text-2xl font-bold text-[#A87608]">{plan.claim_experience.company_rating}</p>
                          <p className="text-xs text-[#3A4557] mt-1">Based on customer reviews</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 p-4 bg-[#EDF1F8]/20 rounded-lg border border-blue-500/10">
                      <p className="text-sm text-[#3A4557]">
                        <strong className="text-[#1B2A4A]">Source:</strong> Insurance Regulatory and Development Authority of India (IRDAI) Annual Report FY 2023-24. 
                        Claim Settlement Ratio indicates the percentage of claims settled out of total claims received.
                      </p>
                    </div>
                  </ExpandableSection>
                )}
              </div>
            )}

            {/* PRODUCT VIDEO */}
            {activeTab === "video" && plan.marketing_video && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                      <Play className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Product Explainer Video
                  </h2>
                  <p className="text-[#3A4557]">Watch this video to understand {plan.name} better</p>
                </div>
                
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-[#1B2A4A] mb-4">{plan.marketing_video.title || "Feature Explainer"}</h3>
                  <div className="relative rounded-xl overflow-hidden bg-black/50 border border-[#C9D4E6]">
                    <video 
                      controls 
                      className="w-full"
                      style={{ maxHeight: '70vh' }}
                      poster=""
                      preload="metadata"
                      data-testid="marketing-video"
                    >
                      <source src={plan.marketing_video.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <p className="text-[#3A4557] text-sm mt-4 text-center">
                    Having trouble viewing? <a href={plan.marketing_video.url} target="_blank" rel="noopener noreferrer" className="text-[#A87608] hover:underline">Open in new tab</a>
                  </p>
                </div>
              </div>
            )}

            {/* CLAIM EXPERIENCE - IRDAI 2024-25 Data */}
            {activeTab === "claim-experience" && (
              <div className="space-y-4" data-testid="claim-experience-section">
                {/* Header */}
                <div className="glass-card p-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <Activity className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    {isTermPlan ? 'Death Claim Settlement Experience' : 'Company Claim Experience'}
                  </h2>
                  <p className="text-[#3A4557]">Official IRDAI data for {plan.company} (FY 2024-25)</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-600 text-xs rounded-full">
                      Source: {isTermPlan ? 'IRDAI Annual Report 2024-25' : 'IRDAI Insurance Statistics Handbook 2024-25'}
                    </span>
                    <span className="px-2 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] text-xs rounded-full">
                      Published: February 2025
                    </span>
                  </div>
                </div>

                {/* TERM INSURANCE - Life Insurance Specific Metrics */}
                {isTermPlan ? (
                  <>
                    {/* Death Claim Settlement - Primary Metric */}
                    <div className="glass-card p-6 bg-gradient-to-r from-green-500/5 to-emerald-500/5 border-green-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                          <CheckCircle2 className="h-6 w-6 text-[#1B2A4A]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-[#1B2A4A]">Death Claim Settlement Ratio</h3>
                          <p className="text-sm text-[#3A4557]">Most important metric for term insurance</p>
                        </div>
                      </div>
                      <div className="text-5xl font-bold text-emerald-600 mb-3">
                        {plan.irdai_data?.death_claim_settlement_ratio ? `${plan.irdai_data.death_claim_settlement_ratio}%` : 'N/A'}
                      </div>
                      <div className="w-full bg-[#EDF1F8]/50 rounded-full h-3 mb-3">
                        <div 
                          className={`h-3 rounded-full ${
                            (plan.irdai_data?.death_claim_settlement_ratio || 0) >= 98 ? 'bg-emerald-500' :
                            (plan.irdai_data?.death_claim_settlement_ratio || 0) >= 95 ? 'bg-green-500' :
                            (plan.irdai_data?.death_claim_settlement_ratio || 0) >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(plan.irdai_data?.death_claim_settlement_ratio || 0, 100)}%` }}
                        />
                      </div>
                      <p className="text-sm text-[#3A4557]">
                        {(plan.irdai_data?.death_claim_settlement_ratio || 0) >= 98 ? '✅ Excellent - Industry leader in claim settlement' :
                         (plan.irdai_data?.death_claim_settlement_ratio || 0) >= 95 ? '✅ Very Good - Reliable claim settlement' :
                         (plan.irdai_data?.death_claim_settlement_ratio || 0) >= 90 ? '⚠️ Average - Some claims may be rejected' : '❌ Below average - High rejection risk'}
                      </p>
                    </div>

                    {/* Main Metrics Grid - Term Insurance */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Claims Settled Within 30 Days */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#EDF1F8]/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                              <Timer className="h-4 w-4 text-[#2E3B57]" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Settled Within 30 Days</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="% claims settled within 30 days of documentation" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.death_claim_settlement_within_30_days ? `${plan.irdai_data.death_claim_settlement_within_30_days}%` : 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          {(plan.irdai_data?.death_claim_settlement_within_30_days || 0) >= 98 ? '🟢 Fast settlement process' :
                           (plan.irdai_data?.death_claim_settlement_within_30_days || 0) >= 95 ? '🟢 Good turnaround time' : '🟡 May take longer'}
                        </p>
                      </div>

                      {/* Average Settlement Time */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                              <Zap className="h-4 w-4 text-purple-600" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Avg. Settlement Time</span>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.average_claim_settlement_time || 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">From complete documentation</p>
                      </div>

                      {/* Solvency Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#D4A017]/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-[#D4A017]/15 flex items-center justify-center">
                              <Scale className="h-4 w-4 text-[#A87608]" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Solvency Ratio</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="Financial strength - IRDAI requires min 1.5" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.solvency_ratio ? `${plan.irdai_data.solvency_ratio}x` : 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          {(plan.irdai_data?.solvency_ratio || 0) >= 2 ? '🟢 Excellent financial health' :
                           (plan.irdai_data?.solvency_ratio || 0) >= 1.5 ? '🟢 Meets IRDAI requirement' : '🔴 Below IRDAI minimum'}
                        </p>
                      </div>

                      {/* Total Claims Settled */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                              <BarChart3 className="h-4 w-4 text-cyan-600" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Individual Claims Settled</span>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.individual_claims_settled ? plan.irdai_data.individual_claims_settled.toLocaleString() : 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          Amount: ₹{plan.irdai_data?.individual_claims_amount_cr ? `${plan.irdai_data.individual_claims_amount_cr} Cr` : 'N/A'}
                        </p>
                      </div>

                      {/* Grievance Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Grievance Ratio</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="Complaints per 10,000 policies - Lower is better" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.grievance_ratio ? plan.irdai_data.grievance_ratio : 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          {(plan.irdai_data?.grievance_ratio || 100) <= 10 ? '🟢 Low complaints' :
                           (plan.irdai_data?.grievance_ratio || 100) <= 20 ? '🟡 Average complaints' : '🔴 High complaints'}
                        </p>
                      </div>

                      {/* Persistency Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Persistency (13-month)</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="% policies renewed after 1 year - Higher is better" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.persistency_13_month ? `${plan.irdai_data.persistency_13_month}%` : 'N/A'}
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          61-month: {plan.irdai_data?.persistency_61_month ? `${plan.irdai_data.persistency_61_month}%` : 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Important Note for Term Insurance */}
                    <div className="glass-card p-5 bg-[#EDF1F8]/20 border-[#C9D4E6]">
                      <h4 className="font-semibold text-[#2E3B57] mb-3 flex items-center gap-2">
                        <Info className="h-5 w-5" /> What These Metrics Mean for You
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4 text-sm text-[#2E3B57]">
                        <div>
                          <p className="font-medium text-[#1B2A4A] mb-1">Death Claim Settlement Ratio</p>
                          <p>The most critical metric - shows what % of death claims are actually paid. Higher is better. Look for 98%+.</p>
                        </div>
                        <div>
                          <p className="font-medium text-[#1B2A4A] mb-1">Solvency Ratio</p>
                          <p>Indicates company's ability to pay future claims. IRDAI mandates minimum 1.5x. Higher means more financial stability.</p>
                        </div>
                        <div>
                          <p className="font-medium text-[#1B2A4A] mb-1">Settlement Time</p>
                          <p>How quickly your family will receive money after claim. Faster is better during difficult times.</p>
                        </div>
                        <div>
                          <p className="font-medium text-[#1B2A4A] mb-1">Persistency Ratio</p>
                          <p>Shows customer satisfaction - higher % means more customers continue their policies.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* HEALTH INSURANCE - Original Health Insurance Metrics */
                  <>
                    {/* Main Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Claim Settlement Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Claim Settlement Ratio</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="% of claims paid vs total claims decided" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.csr ? `${plan.irdai_data.csr}%` : 
                           plan.claim_experience?.claim_settlement_ratio || 'N/A'}
                        </div>
                        <div className="w-full bg-[#EDF1F8]/50 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (plan.irdai_data?.csr || 0) >= 95 ? 'bg-emerald-500' :
                              (plan.irdai_data?.csr || 0) >= 90 ? 'bg-green-500' :
                              (plan.irdai_data?.csr || 0) >= 85 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(plan.irdai_data?.csr || 0, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          {(plan.irdai_data?.csr || 0) >= 95 ? '🟢 Excellent - Very high approval rate' :
                           (plan.irdai_data?.csr || 0) >= 90 ? '🟢 Good - High approval rate' :
                           (plan.irdai_data?.csr || 0) >= 85 ? '🟡 Average - Room for improvement' : '🔴 Below average'}
                        </p>
                      </div>

                      {/* Incurred Claims Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#EDF1F8]/20 to-transparent rounded-bl-full" />
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                              <Percent className="h-4 w-4 text-[#2E3B57]" />
                            </div>
                            <span className="text-sm text-[#3A4557]">Incurred Claims Ratio</span>
                          </div>
                          <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="Claims paid vs premiums earned" />
                        </div>
                        <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                          {plan.irdai_data?.icr ? `${plan.irdai_data.icr}%` : 
                           plan.claim_experience?.incurred_claim_ratio || 'N/A'}
                        </div>
                        <div className="w-full bg-[#EDF1F8]/50 rounded-full h-2 mb-2">
                          <div 
                            className={`h-2 rounded-full ${
                              (plan.irdai_data?.icr || 0) <= 70 ? 'bg-emerald-500' :
                              (plan.irdai_data?.icr || 0) <= 85 ? 'bg-green-500' :
                              (plan.irdai_data?.icr || 0) <= 95 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(plan.irdai_data?.icr || 0, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#3A4557]">
                          {(plan.irdai_data?.icr || 0) <= 70 ? '🟢 Excellent - Healthy claims ratio' :
                           (plan.irdai_data?.icr || 0) <= 85 ? '🟢 Good - Sustainable claims' :
                           (plan.irdai_data?.icr || 0) <= 95 ? '🟡 High - Monitor premium adequacy' : '🔴 Very High - May indicate losses'}
                        </p>
                      </div>

                      {/* Solvency Ratio */}
                      <div className="glass-card p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#D4A017]/20 to-transparent rounded-bl-full" />
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-[#D4A017]/15 flex items-center justify-center">
                          <Shield className="h-4 w-4 text-[#A87608]" />
                        </div>
                        <span className="text-sm text-[#3A4557]">Solvency Ratio</span>
                      </div>
                      <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="Financial health indicator (min 1.5 required)" />
                    </div>
                    <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                      {plan.irdai_data?.solvency_ratio ? plan.irdai_data.solvency_ratio.toFixed(2) : 'N/A'}
                    </div>
                    <div className="w-full bg-[#EDF1F8]/50 rounded-full h-2 mb-2">
                      <div 
                        className={`h-2 rounded-full ${
                          (plan.irdai_data?.solvency_ratio || 0) >= 2.0 ? 'bg-emerald-500' :
                          (plan.irdai_data?.solvency_ratio || 0) >= 1.5 ? 'bg-green-500' :
                          (plan.irdai_data?.solvency_ratio || 0) > 0 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((plan.irdai_data?.solvency_ratio || 0) * 25, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-[#3A4557]">
                      {(plan.irdai_data?.solvency_ratio || 0) >= 2.0 ? '🟢 Excellent - Very financially secure' :
                       (plan.irdai_data?.solvency_ratio || 0) >= 1.5 ? '🟢 Good - Meets IRDAI requirement' :
                       (plan.irdai_data?.solvency_ratio || 0) > 0 ? '🟡 Low - Below recommended level' : '🔴 Negative - Financial stress'}
                    </p>
                  </div>

                  {/* Complaints */}
                  <div className="glass-card p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-rose-500/20 to-transparent rounded-bl-full" />
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                          <MessageCircle className="h-4 w-4 text-rose-600" />
                        </div>
                        <span className="text-sm text-[#3A4557]">Complaints (FY 2024-25)</span>
                      </div>
                      <Info className="h-4 w-4 text-[#3A4557] cursor-help" title="Grievances reported to IRDAI" />
                    </div>
                    <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                      {plan.irdai_data?.complaints ? plan.irdai_data.complaints.toLocaleString() : 'N/A'}
                    </div>
                    <p className="text-xs text-[#3A4557] mt-2">
                      {(plan.irdai_data?.complaints || 0) < 3000 ? '🟢 Low complaint volume' :
                       (plan.irdai_data?.complaints || 0) < 8000 ? '🟡 Moderate complaints' : '🔴 High volume - larger market share'}
                    </p>
                    <p className="text-xs text-[#3A4557] mt-1">
                      Note: Higher complaints often correlate with larger market share
                    </p>
                  </div>

                  {/* Network Hospitals */}
                  <div className="glass-card p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-bl-full" />
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                          <Building2 className="h-4 w-4 text-cyan-600" />
                        </div>
                        <span className="text-sm text-[#3A4557]">Network Hospitals</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                      {plan.irdai_data?.network_hospitals ? plan.irdai_data.network_hospitals.toLocaleString() + '+' : 
                       plan.network_hospitals || 'N/A'}
                    </div>
                    <p className="text-sm text-cyan-600">
                      Cashless: {plan.irdai_data?.cashless_hospitals ? plan.irdai_data.cashless_hospitals.toLocaleString() + '+' : 'N/A'}
                    </p>
                    <p className="text-xs text-[#3A4557] mt-2">
                      {(plan.irdai_data?.network_hospitals || 0) >= 15000 ? '🟢 Excellent network coverage' :
                       (plan.irdai_data?.network_hospitals || 0) >= 10000 ? '🟢 Good network coverage' :
                       (plan.irdai_data?.network_hospitals || 0) >= 5000 ? '🟡 Average network' : '🔴 Limited network'}
                    </p>
                  </div>

                  {/* Gross Premium / Market Position */}
                  <div className="glass-card p-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/20 to-transparent rounded-bl-full" />
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <IndianRupee className="h-4 w-4 text-purple-600" />
                        </div>
                        <span className="text-sm text-[#3A4557]">Gross Premium (FY 24-25)</span>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-[#1B2A4A] mb-2">
                      {plan.irdai_data?.gross_premium_cr ? `₹${plan.irdai_data.gross_premium_cr.toLocaleString()} Cr` : 'N/A'}
                    </div>
                    <p className="text-xs text-[#3A4557] mt-2">
                      {(plan.irdai_data?.gross_premium_cr || 0) >= 10000 ? '🟢 Market Leader' :
                       (plan.irdai_data?.gross_premium_cr || 0) >= 5000 ? '🟢 Major Player' :
                       (plan.irdai_data?.gross_premium_cr || 0) >= 1000 ? '🟡 Growing Insurer' : '🔵 Emerging Player'}
                    </p>
                  </div>
                </div>

                {/* Understanding the Metrics */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-[#A87608]" />
                    Understanding These Metrics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-[#EDF1F8]/50 p-4 rounded-lg border border-[#C9D4E6]/50">
                      <h4 className="font-semibold text-emerald-600 mb-2">Claim Settlement Ratio (CSR)</h4>
                      <p className="text-[#3A4557]">
                        Percentage of claims PAID out of total claims DECIDED (paid + rejected). 
                        <span className="text-emerald-600"> Higher is better.</span> A CSR of 95%+ indicates excellent claim approval.
                      </p>
                    </div>
                    <div className="bg-[#EDF1F8]/50 p-4 rounded-lg border border-[#C9D4E6]/50">
                      <h4 className="font-semibold text-[#2E3B57] mb-2">Incurred Claims Ratio (ICR)</h4>
                      <p className="text-[#3A4557]">
                        Claims paid as % of premiums earned. <span className="text-[#2E3B57]">Lower is healthier</span> for the insurer. 
                        ICR above 100% means the company is paying more in claims than it earns.
                      </p>
                    </div>
                    <div className="bg-[#EDF1F8]/50 p-4 rounded-lg border border-[#C9D4E6]/50">
                      <h4 className="font-semibold text-[#A87608] mb-2">Solvency Ratio</h4>
                      <p className="text-[#3A4557]">
                        Financial strength indicator. IRDAI mandates minimum <span className="text-[#A87608]">1.5</span>. 
                        Higher solvency means better ability to pay future claims. Values above 2.0 are excellent.
                      </p>
                    </div>
                    <div className="bg-[#EDF1F8]/50 p-4 rounded-lg border border-[#C9D4E6]/50">
                      <h4 className="font-semibold text-rose-600 mb-2">Complaints</h4>
                      <p className="text-[#3A4557]">
                        Grievances reported to IRDAI during the year. <span className="text-rose-600">Context matters</span> - 
                        larger insurers with more customers naturally have more complaints. Look at the ratio, not just the number.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="glass-card p-4 bg-[#EDF1F8]/30 border-[#C9D4E6]/50">
                  <p className="text-xs text-[#3A4557] flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-[#A87608] flex-shrink-0 mt-0.5" />
                    <span>
                      Data sourced from IRDAI Insurance Statistics Handbook 2024-25 (Published February 2025). 
                      This data reflects the overall company performance and not specific to individual plans. 
                      Past performance is not indicative of future results.
                    </span>
                  </p>
                </div>
                  </>
                )}
              </div>
            )}

            {/* MATERNITY BENEFITS - Comprehensive Section for Star Women Care */}
            {activeTab === "maternity" && (plan.maternity_coverage || plan.maternity_benefits || plan.id === 'star-women-care') && (
              <div className="space-y-4">
                {/* Header */}
                <div className="glass-card p-6 bg-gradient-to-r from-pink-500/10 to-rose-500/10 border-pink-500/30">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center">
                      <Baby className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Comprehensive Maternity & Pregnancy Coverage
                  </h2>
                  <p className="text-[#3A4557]">Complete details on pregnancy, delivery, newborn, and fertility benefits</p>
                  {plan.maternity_benefits && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">{plan.maternity_benefits.availability}</span>
                      {plan.maternity_benefits.surrogacy?.covered && (
                        <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">Surrogacy Covered ✓</span>
                      )}
                      {plan.maternity_benefits.art_complications?.covered && (
                        <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">IVF/ART Complications ✓</span>
                      )}
                    </div>
                  )}
                  {!plan.maternity_benefits && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">Entry During Pregnancy ✓</span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">Newborn from Day 1 ✓</span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-700 rounded-full text-sm">IVF/ART Covered ✓</span>
                    </div>
                  )}
                </div>

                {/* Bajaj My Health Care Plan - Dynamic Maternity Benefits */}
                {plan.maternity_benefits && (
                  <>
                    {/* 1. Delivery Expenses */}
                    <ExpandableSection
                      title="Delivery Expenses (Normal & Caesarean)"
                      icon={Baby}
                      iconColor="from-pink-500 to-rose-400"
                      defaultOpen={true}
                      badge={`Up to ${Object.values(plan.maternity_benefits.limits || {})[Object.keys(plan.maternity_benefits.limits || {}).length - 1] || 'As per SI'}`}
                    >
                      <div className="space-y-4">
                        <p className="text-[#2E3B57] leading-relaxed">
                          {plan.maternity_benefits.coverage_includes?.join('. ') || 'Covers all expenses related to Normal and Caesarean deliveries including pre-natal and post-natal expenses.'}
                        </p>
                        
                        <div className="bg-pink-500/10 rounded-xl p-4 border border-pink-500/20">
                          <h4 className="font-semibold text-pink-600 mb-3">What's Covered:</h4>
                          <ul className="space-y-2 text-[#2E3B57]">
                            {(plan.maternity_benefits.coverage_includes || []).map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {plan.maternity_benefits.limits && (
                          <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                            <h4 className="font-semibold text-[#1B2A4A] mb-3">Coverage Limits by Sum Insured:</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {Object.entries(plan.maternity_benefits.limits).map(([si, limit], idx) => (
                                <div key={idx} className="bg-pink-500/10 rounded-lg p-3 text-center">
                                  <p className="text-[#3A4557] text-xs">{si} SI</p>
                                  <p className="text-pink-600 font-bold">{limit}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                          <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                            <Clock className="h-4 w-4" /> Waiting Period:
                          </h4>
                          <p className="text-[#2E3B57]">{plan.maternity_benefits.waiting_period}</p>
                          {plan.maternity_benefits.age_limit && (
                            <p className="text-[#3A4557] text-sm mt-1">Age Limit: {plan.maternity_benefits.age_limit}</p>
                          )}
                        </div>
                      </div>
                    </ExpandableSection>

                    {/* 2. Surrogacy Coverage */}
                    {plan.maternity_benefits.surrogacy?.covered && (
                      <ExpandableSection
                        title="Surrogacy Coverage"
                        icon={Heart}
                        iconColor="from-purple-500 to-pink-400"
                        badge="Covered"
                      >
                        <div className="space-y-3">
                          <p className="text-[#2E3B57]">{plan.maternity_benefits.surrogacy.details}</p>
                          <div className="bg-purple-500/10 rounded-xl p-3 border border-purple-500/20">
                            <p className="text-purple-700 text-sm">All terms and conditions of regular maternity coverage apply.</p>
                          </div>
                        </div>
                      </ExpandableSection>
                    )}

                    {/* 3. ART/IVF Complications */}
                    {plan.maternity_benefits.art_complications?.covered && (
                      <ExpandableSection
                        title="Assisted Reproductive Technology (ART) Complications"
                        icon={Heart}
                        iconColor="from-rose-500 to-orange-400"
                        badge="Covered"
                      >
                        <div className="space-y-3">
                          <p className="text-[#2E3B57]">{plan.maternity_benefits.art_complications.details}</p>
                          {plan.maternity_benefits.art_complications.oocyte_donor && (
                            <div className="bg-rose-500/10 rounded-xl p-3 border border-rose-500/20">
                              <h4 className="font-semibold text-rose-600 mb-2">Oocyte Donor Coverage:</h4>
                              <p className="text-[#2E3B57] text-sm">{plan.maternity_benefits.art_complications.oocyte_donor}</p>
                            </div>
                          )}
                        </div>
                      </ExpandableSection>
                    )}
                  </>
                )}

                {/* Star Women Care Plan - Existing hardcoded content */}
                {!plan.maternity_benefits && (
                  <>
                    {/* 1. Delivery Expenses for Star Women Care */}
                    <ExpandableSection
                      title="Delivery Expenses (Normal & Caesarean)"
                      icon={Baby}
                      iconColor="from-pink-500 to-rose-400"
                      defaultOpen={true}
                      badge="Up to ₹1 Lakh"
                    >
                      <div className="space-y-4">
                        <p className="text-[#2E3B57] leading-relaxed">
                          Covers all expenses related to Normal and Caesarean deliveries including pre-natal and post-natal expenses.
                          Maximum 2 deliveries covered in lifetime.
                        </p>
                        
                        <div className="bg-pink-500/10 rounded-xl p-4 border border-pink-500/20">
                          <h4 className="font-semibold text-pink-600 mb-3">What's Covered:</h4>
                          <ul className="space-y-2 text-[#2E3B57]">
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Normal vaginal delivery</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Caesarean section (C-section)</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Pre-natal expenses (before delivery)</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Post-natal expenses (after delivery)</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Doctor and surgeon fees</li>
                            <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-pink-600 mt-1 flex-shrink-0" />Anesthesia & OT charges</li>
                          </ul>
                        </div>

                        <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                          <h4 className="font-semibold text-[#1B2A4A] mb-3">Coverage Limits by Sum Insured:</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-pink-500/10 rounded-lg p-3 text-center">
                              <p className="text-[#3A4557] text-xs">₹5 Lakh SI</p>
                              <p className="text-pink-600 font-bold">₹25,000</p>
                            </div>
                            <div className="bg-pink-500/10 rounded-lg p-3 text-center">
                              <p className="text-[#3A4557] text-xs">₹10-20L SI</p>
                              <p className="text-pink-600 font-bold">₹50,000</p>
                            </div>
                            <div className="bg-pink-500/10 rounded-lg p-3 text-center">
                              <p className="text-[#3A4557] text-xs">₹25-50L SI</p>
                              <p className="text-pink-600 font-bold">₹75,000</p>
                            </div>
                            <div className="bg-pink-500/10 rounded-lg p-3 text-center">
                              <p className="text-[#3A4557] text-xs">₹1 Crore SI</p>
                              <p className="text-pink-600 font-bold">₹1,00,000</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                          <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                            <Clock className="h-4 w-4" /> Waiting Period:
                          </h4>
                          <ul className="text-[#2E3B57] space-y-1 text-sm">
                            <li>• <strong>₹5-10 Lakh SI:</strong> 24 months from policy inception</li>
                            <li>• <strong>₹15 Lakh+ SI:</strong> 12 months from policy inception</li>
                          </ul>
                        </div>

                        <div className="bg-red-500/10 rounded-xl p-3 border border-red-500/20">
                          <p className="text-red-600 text-sm flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <span><strong>Note:</strong> Pre & Post hospitalization benefits are NOT applicable for delivery expenses.</span>
                          </p>
                        </div>
                      </div>
                    </ExpandableSection>

                {/* 2. Newborn Baby Coverage */}
                <ExpandableSection
                  title="Newborn Baby Coverage (from Day 1)"
                  icon={Baby}
                  iconColor="from-[#EDF1F8] to-cyan-500"
                  badge="UNIQUE BENEFIT"
                >
                  <div className="space-y-4">
                    <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                      <p className="text-emerald-600 font-semibold mb-2">★ Coverage starts from Day 1 of birth!</p>
                      <p className="text-[#2E3B57] text-sm">Most other plans start newborn coverage only from Day 16 or Day 91. This plan covers from Day 1.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                        <h4 className="font-semibold text-[#2E3B57] mb-3">First Year Coverage (Birth Year)</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E3B57] mt-0.5 flex-shrink-0" />Up to 25% of Sum Insured</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E3B57] mt-0.5 flex-shrink-0" />All hospitalization expenses</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E3B57] mt-0.5 flex-shrink-0" />Congenital internal defects covered</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E3B57] mt-0.5 flex-shrink-0" />Congenital anomalies covered</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#2E3B57] mt-0.5 flex-shrink-0" />NICU/ICU charges covered</li>
                        </ul>
                        <p className="mt-2 text-xs text-[#3A4557]">Example: For ₹10L SI, newborn covered up to ₹2.5L in first year</p>
                      </div>
                      <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                        <h4 className="font-semibold text-emerald-600 mb-3">Subsequent Years Coverage</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />Up to 100% of Sum Insured</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />Full hospitalization coverage</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />All defects and anomalies covered</li>
                        </ul>
                        <p className="mt-2 text-xs text-[#A87608]">Requires: Additional premium payment for adding newborn to policy</p>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 3. Vaccination Coverage */}
                <ExpandableSection
                  title="Newborn Vaccination Coverage"
                  icon={Pill}
                  iconColor="from-green-500 to-emerald-500"
                  badge="Up to ₹3,500"
                >
                  <div className="space-y-4">
                    <p className="text-[#2E3B57] leading-relaxed">
                      Vaccination expenses for the newborn baby are payable up to 12 months from the birth of the newborn.
                    </p>
                    
                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-[#A87608] mt-0.5 flex-shrink-0" />
                        <p className="text-[#8F6A06] text-sm">
                          <strong>Note:</strong> The official policy wording does not specify particular vaccines. All medically necessary vaccinations for newborns are covered subject to the limits below.
                        </p>
                      </div>
                    </div>

                    <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Coverage Limits by Sum Insured:</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹5L & ₹10L SI</p>
                          <p className="text-emerald-600 font-bold">₹2,500</p>
                        </div>
                        <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹15L and above</p>
                          <p className="text-emerald-600 font-bold">₹3,500</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 4. IVF/ART Coverage */}
                <ExpandableSection
                  title="IVF & Assisted Reproduction Treatment (ART)"
                  icon={Heart}
                  iconColor="from-purple-500 to-violet-500"
                  badge="Up to ₹3 Lakh"
                >
                  <div className="space-y-4">
                    <p className="text-[#2E3B57] leading-relaxed">
                      Comprehensive coverage for fertility treatments to help couples achieve parenthood.
                    </p>
                    
                    <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                      <h4 className="font-semibold text-purple-600 mb-3">Treatments Covered:</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        <ul className="space-y-2 text-[#2E3B57]">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /><strong>IVF</strong> - In-Vitro Fertilisation</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /><strong>ICSI</strong> - Intracytoplasmic Sperm Injection</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /><strong>IUI</strong> - Intrauterine Insemination</li>
                        </ul>
                        <ul className="space-y-2 text-[#2E3B57]">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /><strong>TESA/TESE</strong> - Sperm Aspiration/Extraction</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" />Egg retrieval procedures</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" />Embryo transfer</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Coverage Limits (Per Policy Year):</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹5 Lakh SI</p>
                          <p className="text-purple-600 font-bold">₹50,000</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹10 Lakh SI</p>
                          <p className="text-purple-600 font-bold">₹1,00,000</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹15 Lakh SI</p>
                          <p className="text-purple-600 font-bold">₹1,50,000</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹20-25L SI</p>
                          <p className="text-purple-600 font-bold">₹2,00,000</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹50L SI</p>
                          <p className="text-purple-600 font-bold">₹2,50,000</p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹1 Crore SI</p>
                          <p className="text-purple-600 font-bold">₹3,00,000</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <h4 className="font-semibold text-[#A87608] mb-2 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Waiting Period: 36 months from first policy inception
                      </h4>
                      <p className="text-[#2E3B57] text-sm">One cycle of treatment covered per policy year. Must be at centers registered with National ART and Surrogacy Registry.</p>
                    </div>

                    <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                      <h4 className="font-semibold text-red-600 mb-2">What's NOT Covered:</h4>
                      <ul className="text-[#2E3B57] text-sm space-y-1">
                        <li>• Pre and post treatment expenses</li>
                        <li>• Unproven/experimental treatments</li>
                        <li>• Reversal of voluntary sterilization</li>
                        <li>• Cryopreservation and storage costs</li>
                        <li>• Services at unrecognized centers</li>
                      </ul>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 5. Ante-Natal Care */}
                <ExpandableSection
                  title="Ante-Natal Care (Pregnancy OPD)"
                  icon={Stethoscope}
                  iconColor="from-cyan-500 to-[#DCE4F2]"
                  badge="Up to ₹5,000/year"
                >
                  <div className="space-y-4">
                    <p className="text-[#2E3B57] leading-relaxed">
                      Out-patient expenses during pregnancy covered after confirmation of pregnancy.
                    </p>
                    
                    <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20">
                      <h4 className="font-semibold text-cyan-600 mb-3">What's Covered:</h4>
                      <ul className="space-y-2 text-[#2E3B57]">
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Doctor consultations during pregnancy</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Pregnancy tests and lab work</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Ultrasound scans</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Routine check-ups</li>
                        <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Vitamins and supplements (prescribed)</li>
                      </ul>
                    </div>

                    <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Coverage Limits:</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹5-15L SI</p>
                          <p className="text-cyan-600 font-bold">₹2,500/year</p>
                        </div>
                        <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹20-25L SI</p>
                          <p className="text-cyan-600 font-bold">₹3,500/year</p>
                        </div>
                        <div className="bg-cyan-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹50L-1Cr SI</p>
                          <p className="text-cyan-600 font-bold">₹5,000/year</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <h4 className="font-semibold text-[#A87608] mb-2">Waiting Period:</h4>
                      <ul className="text-[#2E3B57] text-sm space-y-1">
                        <li>• <strong>₹5-10 Lakh SI:</strong> 24 months</li>
                        <li>• <strong>₹15 Lakh+ SI:</strong> 12 months</li>
                      </ul>
                      <p className="text-emerald-600 text-sm mt-2">✓ Does NOT affect cumulative bonus</p>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 6. Surrogacy & Oocyte Donor */}
                <ExpandableSection
                  title="Surrogacy & Oocyte Donor Coverage"
                  icon={Users}
                  iconColor="from-indigo-500 to-purple-500"
                >
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-indigo-500/10 rounded-xl p-4 border border-indigo-500/20">
                        <h4 className="font-semibold text-indigo-600 mb-3">Surrogate Mother Coverage</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-indigo-600 mt-1 flex-shrink-0" />Post-partum delivery complications</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-indigo-600 mt-1 flex-shrink-0" />Duration: 36 months from treatment</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-indigo-600 mt-1 flex-shrink-0" />Same limits as ART treatment</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-indigo-600 mt-1 flex-shrink-0" />Lump sum for miscarriage (accident) - No waiting</li>
                        </ul>
                      </div>
                      <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                        <h4 className="font-semibold text-purple-600 mb-3">Oocyte (Egg) Donor Coverage</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" />Complications from ART procedures</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" />Duration: 12 months from treatment</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" />Same limits as ART treatment</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <h4 className="font-semibold text-[#A87608] mb-2">Important Conditions:</h4>
                      <ul className="text-[#2E3B57] text-sm space-y-1">
                        <li>• Proposal must be made 30 days before embryo transfer</li>
                        <li>• Must comply with Surrogacy Act and ART Act 2021</li>
                        <li>• Ante-natal care & delivery expenses NOT payable for surrogate</li>
                      </ul>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 7. In Utero Fetal Surgery */}
                <ExpandableSection
                  title="In Utero Fetal Surgery/Repair"
                  icon={Activity}
                  iconColor="from-rose-500 to-pink-500"
                >
                  <div className="space-y-4">
                    <p className="text-[#2E3B57] leading-relaxed">
                      Advanced surgical procedures to correct fetal abnormalities before birth.
                    </p>
                    
                    <div className="bg-rose-500/10 rounded-xl p-4 border border-rose-500/20">
                      <h4 className="font-semibold text-rose-600 mb-3">Types of Surgeries Covered:</h4>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-rose-700 font-medium mb-2">Open Fetal Surgery:</p>
                          <p className="text-[#2E3B57]">Hysterotomy, CPAM Lobectomy</p>
                        </div>
                        <div>
                          <p className="text-rose-700 font-medium mb-2">Fetendo Fetal Surgery:</p>
                          <p className="text-[#2E3B57]">Balloon occlusion, Laser ablation</p>
                        </div>
                        <div>
                          <p className="text-rose-700 font-medium mb-2">Image-Guided Surgery:</p>
                          <p className="text-[#2E3B57]">Amnioreduction, Fetal blood sampling</p>
                        </div>
                        <div>
                          <p className="text-rose-700 font-medium mb-2">EXIT Procedures:</p>
                          <p className="text-[#2E3B57]">Specialized delivery procedures</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Conditions Covered:</h4>
                      <div className="flex flex-wrap gap-2">
                        {['Spina bifida', 'Twin-twin transfusion (TTTS)', 'Congenital diaphragmatic hernia', 'Amniotic band syndrome', 'Bronchopulmonary sequestration'].map(condition => (
                          <span key={condition} className="px-3 py-1 bg-rose-500/20 text-rose-700 rounded-full text-sm">{condition}</span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <h4 className="font-semibold text-[#A87608] mb-2">Waiting Period:</h4>
                      <ul className="text-[#2E3B57] text-sm space-y-1">
                        <li>• <strong>₹5-10 Lakh SI:</strong> 24 months</li>
                        <li>• <strong>₹15 Lakh+ SI:</strong> 12 months</li>
                      </ul>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 8. Miscarriage Coverage */}
                <ExpandableSection
                  title="Miscarriage Due to Accident Coverage"
                  icon={AlertTriangle}
                  iconColor="from-[#D4A017] to-[#D9A82C]"
                >
                  <div className="space-y-4">
                    <p className="text-[#2E3B57] leading-relaxed">
                      Lump sum benefit for miscarriage resulting from accidental injury. Available only once in lifetime.
                    </p>
                    
                    <div className="bg-[#EDF1F8]/60 rounded-xl p-4">
                      <h4 className="font-semibold text-[#1B2A4A] mb-3">Lump Sum Benefits:</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-orange-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹5-15L SI</p>
                          <p className="text-orange-600 font-bold">₹25,000</p>
                        </div>
                        <div className="bg-orange-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹20-25L SI</p>
                          <p className="text-orange-600 font-bold">₹35,000</p>
                        </div>
                        <div className="bg-orange-500/10 rounded-lg p-3 text-center">
                          <p className="text-[#3A4557] text-xs">₹50L-1Cr SI</p>
                          <p className="text-orange-600 font-bold">₹40,000</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                      <h4 className="font-semibold text-[#A87608] mb-2">Waiting Period:</h4>
                      <ul className="text-[#2E3B57] text-sm space-y-1">
                        <li>• <strong>₹5-10 Lakh SI:</strong> 24 months</li>
                        <li>• <strong>₹15 Lakh+ SI:</strong> 12 months</li>
                        <li>• <strong>Surrogacy Cases:</strong> No waiting period</li>
                      </ul>
                    </div>

                    <div className="bg-red-500/10 rounded-xl p-3 border border-red-500/20">
                      <p className="text-red-600 text-sm">
                        <strong>Note:</strong> Must be due to accident only. Forms part of sum insured and impacts bonus.
                      </p>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 9. Additional Benefits */}
                <ExpandableSection
                  title="Additional Newborn & Child Benefits"
                  icon={Heart}
                  iconColor="from-teal-500 to-cyan-500"
                >
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-teal-500/10 rounded-xl p-4 border border-teal-500/20">
                        <h4 className="font-semibold text-teal-600 mb-3">Metabolic Screening Test</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />Up to ₹3,500 per newborn</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />Once per child</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />Screens for phenylketonuria, hypothyroidism</li>
                        </ul>
                      </div>
                      <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20">
                        <h4 className="font-semibold text-cyan-600 mb-3">Pediatrician Consultations</h4>
                        <ul className="space-y-2 text-[#2E3B57] text-sm">
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />Up to 4 consultations per year</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />₹500 per consultation</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-cyan-600 mt-1 flex-shrink-0" />For children up to 12 years</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-pink-500/10 rounded-xl p-4 border border-pink-500/20">
                      <h4 className="font-semibold text-pink-600 mb-3">Star Mother Cover (Unique)</h4>
                      <p className="text-[#2E3B57] text-sm">
                        Mother's room and boarding expenses covered when insured child is admitted to ICU. 
                        Allows mother to stay with critically ill child without additional cost.
                      </p>
                    </div>
                  </div>
                </ExpandableSection>

                {/* 10. Waiting Periods Summary */}
                <ExpandableSection
                  title="Maternity Waiting Periods Summary"
                  icon={Clock}
                  iconColor="from-[#D4A017] to-[#D9A82C]"
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#C9D4E6]">
                          <th className="text-left py-3 px-4 text-[#3A4557]">Coverage Type</th>
                          <th className="text-center py-3 px-4 text-[#3A4557]">₹5-10L SI</th>
                          <th className="text-center py-3 px-4 text-[#3A4557]">₹15L+ SI</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#2E3B57]">
                        <tr className="border-b border-[#D4DEEC]">
                          <td className="py-3 px-4">IVF/ART Treatment</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">36 months</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">36 months</td>
                        </tr>
                        <tr className="border-b border-[#D4DEEC]">
                          <td className="py-3 px-4">Delivery Expenses</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                          <td className="py-3 px-4 text-center text-emerald-600">12 months</td>
                        </tr>
                        <tr className="border-b border-[#D4DEEC]">
                          <td className="py-3 px-4">Ante-natal Care</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                          <td className="py-3 px-4 text-center text-emerald-600">12 months</td>
                        </tr>
                        <tr className="border-b border-[#D4DEEC]">
                          <td className="py-3 px-4">In Utero Fetal Surgery</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                          <td className="py-3 px-4 text-center text-emerald-600">12 months</td>
                        </tr>
                        <tr className="border-b border-[#D4DEEC]">
                          <td className="py-3 px-4">Miscarriage (Accident)</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                          <td className="py-3 px-4 text-center text-emerald-600">12 months</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4">Voluntary Sterilization</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                          <td className="py-3 px-4 text-center text-[#A87608]">24 months</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </ExpandableSection>

                {/* Maternity Exclusions */}
                <div className="glass-card p-6 bg-red-500/5 border-red-500/20">
                  <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2">
                    <XCircle className="h-5 w-5" /> Maternity-Specific Exclusions
                  </h3>
                  <ul className="space-y-2 text-[#2E3B57]">
                    <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />Miscarriage (unless due to accident)</li>
                    <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />Contraception and sterilization (except voluntary sterilization benefit)</li>
                    <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />Surrogacy services (except specific surrogacy coverage)</li>
                    <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />Fertility enhancement surgeries beyond ART</li>
                    <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />More than 2 deliveries in lifetime</li>
                  </ul>
                </div>
                  </>
                )}

                {/* CTA */}
                <div className="glass-card p-6 bg-gradient-to-r from-pink-500/10 to-purple-500/10 border-pink-500/30 text-center">
                  <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">Need Help Understanding Maternity Benefits?</h3>
                  <p className="text-[#3A4557] mb-4">Our experts can explain coverage details specific to your situation</p>
                  <div className="flex justify-center gap-4">
                    <a href="tel:+919510855001" className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl transition-colors">
                      <Phone className="h-4 w-4" /> Call Now
                    </a>
                    <a href={`https://wa.me/919510855001?text=Hi, I need help understanding maternity benefits in ${plan?.name || 'this insurance plan'}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors">
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* DEATH BENEFIT - Term Insurance Only */}
            {activeTab === "death-benefit" && isTermPlan && (
              <div className="space-y-4" data-testid="death-benefit-section">
                {/* Header */}
                <div className="glass-card p-6 bg-gradient-to-r from-[#EDF1F8]/10 to-cyan-500/10 border-blue-500/30">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-cyan-500 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Death Benefit Structure
                  </h2>
                  <p className="text-[#3A4557]">Understanding what your family receives in case of an unfortunate event</p>
                </div>

                {plan.death_benefit ? (
                  <>
                    {/* Main Calculation Formula */}
                    <div className="glass-card p-6">
                      <h3 className="text-lg font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-[#2E3B57]" />
                        Death Benefit Calculation
                      </h3>
                      <div className="bg-[#EDF1F8]/30 rounded-xl p-5 border border-[#C9D4E6]">
                        <p className="text-[#1B2A4A] font-medium text-lg">{plan.death_benefit.calculation || "Sum Assured on Death"}</p>
                      </div>
                    </div>

                    {/* Payment Type Variations */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {plan.death_benefit.single_pay && (
                        <div className="glass-card p-5">
                          <h4 className="font-semibold text-[#A87608] mb-3 flex items-center gap-2">
                            <IndianRupee className="h-4 w-4" />
                            Single Premium Payment
                          </h4>
                          <p className="text-[#2E3B57] text-sm">{plan.death_benefit.single_pay}</p>
                        </div>
                      )}
                      {plan.death_benefit.limited_regular_pay && (
                        <div className="glass-card p-5">
                          <h4 className="font-semibold text-emerald-600 mb-3 flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Limited/Regular Pay
                          </h4>
                          <p className="text-[#2E3B57] text-sm">{plan.death_benefit.limited_regular_pay}</p>
                        </div>
                      )}
                    </div>

                    {/* SA Factor Options */}
                    {plan.death_benefit.sa_factor_options && (
                      <ExpandableSection
                        title="Sum Assured Factor Options"
                        icon={Layers}
                        iconColor="from-purple-500 to-violet-500"
                        defaultOpen={true}
                      >
                        <div className="space-y-3">
                          {Object.entries(plan.death_benefit.sa_factor_options).map(([key, value]) => (
                            <div key={key} className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
                              <h5 className="font-semibold text-purple-600 mb-2 capitalize">{key.replace(/_/g, ' ')}</h5>
                              <p className="text-[#2E3B57]">{value}</p>
                            </div>
                          ))}
                        </div>
                      </ExpandableSection>
                    )}

                    {/* Additional Death Benefit Features */}
                    {plan.death_benefit.additional_features && (
                      <ExpandableSection
                        title="Additional Death Benefit Features"
                        icon={Shield}
                        iconColor="from-cyan-500 to-[#DCE4F2]"
                        defaultOpen={false}
                      >
                        <ul className="space-y-3">
                          {plan.death_benefit.additional_features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-[#2E3B57]">
                              <CheckCircle2 className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </ExpandableSection>
                    )}
                  </>
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Shield className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Death benefit details not available. Please refer to policy documents.</p>
                  </div>
                )}
              </div>
            )}

            {/* PLAN OPTIONS - Term Insurance Only */}
            {activeTab === "plan-options" && isTermPlan && (
              <div className="space-y-4" data-testid="plan-options-section">
                {/* Header */}
                <div className="glass-card p-6 bg-gradient-to-r from-purple-500/10 to-violet-500/10 border-purple-500/30">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center">
                      <Layers className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Plan Options & Variants
                  </h2>
                  <p className="text-[#3A4557]">Choose the coverage structure that suits your needs</p>
                </div>

                {/* Plan Variants */}
                {plan.variants && plan.variants.length > 0 && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-[#1B2A4A] mb-4">Available Plan Variants</h3>
                    <div className="space-y-4">
                      {plan.variants.map((variant, idx) => (
                        <div key={idx} className="bg-purple-500/10 rounded-xl p-5 border border-purple-500/20">
                          <h4 className="font-bold text-purple-600 text-lg mb-2">{variant.name}</h4>
                          {variant.key_feature && (
                            <p className="text-[#1B2A4A] mb-3">{variant.key_feature}</p>
                          )}
                          {variant.description && variant.description !== variant.key_feature && (
                            <p className="text-[#3A4557] text-sm">{variant.description}</p>
                          )}
                          {variant.coverage_multiplier && (
                            <div className="mt-3 inline-block px-3 py-1 bg-purple-500/20 text-purple-700 rounded-full text-sm">
                              Coverage: {variant.coverage_multiplier}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Policy Options Grid */}
                {plan.policy_options && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-[#1B2A4A] mb-4">Eligibility & Policy Terms</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {plan.policy_options.entry_age && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Entry Age</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.entry_age}</p>
                        </div>
                      )}
                      {plan.policy_options.maturity_age && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Maximum Maturity Age</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.maturity_age}</p>
                        </div>
                      )}
                      {plan.policy_options.policy_term && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Policy Term</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.policy_term}</p>
                        </div>
                      )}
                      {plan.policy_options.premium_payment && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Premium Payment Term</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.premium_payment}</p>
                        </div>
                      )}
                      {plan.policy_options.premium_frequency && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Premium Frequency</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.premium_frequency}</p>
                        </div>
                      )}
                      {plan.policy_options.min_premium && (
                        <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                          <p className="text-[#3A4557] text-sm mb-1">Minimum Premium</p>
                          <p className="text-[#1B2A4A] font-semibold">{plan.policy_options.min_premium}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Sum Insured Options */}
                {plan.sum_insured_options && plan.sum_insured_options.length > 0 && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-[#1B2A4A] mb-4">Sum Assured Options</h3>
                    <div className="flex flex-wrap gap-3">
                      {plan.sum_insured_options.map((si, idx) => (
                        <div key={idx} className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                          <span className="font-bold text-emerald-600">{si}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Important Policy Terms */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-[#1B2A4A] mb-4">Important Policy Terms</h3>
                  <div className="space-y-3">
                    {plan.free_look_period && (
                      <div className="flex justify-between items-start py-3 border-b border-blue-500/10">
                        <span className="text-[#3A4557]">Free Look Period</span>
                        <span className="text-[#1B2A4A] font-medium text-right max-w-[60%]">
                          {typeof plan.free_look_period === 'object' 
                            ? plan.free_look_period.duration || JSON.stringify(plan.free_look_period)
                            : plan.free_look_period}
                        </span>
                      </div>
                    )}
                    {plan.grace_period && (
                      <div className="flex justify-between items-start py-3 border-b border-blue-500/10">
                        <span className="text-[#3A4557]">Grace Period</span>
                        <span className="text-[#1B2A4A] font-medium text-right max-w-[60%]">
                          {typeof plan.grace_period === 'object' 
                            ? (plan.grace_period.yearly_half_quarterly 
                                ? `${plan.grace_period.yearly_half_quarterly} (Yearly/Half-yearly/Quarterly)` 
                                : plan.grace_period.duration || JSON.stringify(plan.grace_period))
                            : plan.grace_period}
                        </span>
                      </div>
                    )}
                    {plan.revival_period && (
                      <div className="flex justify-between items-start py-3 border-b border-blue-500/10">
                        <span className="text-[#3A4557]">Revival Period</span>
                        <span className="text-[#1B2A4A] font-medium text-right max-w-[60%]">
                          {typeof plan.revival_period === 'object' 
                            ? plan.revival_period.duration || JSON.stringify(plan.revival_period)
                            : plan.revival_period}
                        </span>
                      </div>
                    )}
                    {plan.suicide_clause && (
                      <div className="flex justify-between items-start py-3 border-b border-blue-500/10">
                        <span className="text-[#3A4557]">Suicide Clause</span>
                        <span className="text-[#1B2A4A] font-medium text-right max-w-[60%]">
                          {typeof plan.suicide_clause === 'object' 
                            ? plan.suicide_clause.period || JSON.stringify(plan.suicide_clause)
                            : plan.suicide_clause}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* SURRENDER & MATURITY - Term Insurance Only */}
            {activeTab === "surrender" && isTermPlan && (
              <div className="space-y-4" data-testid="surrender-maturity-section">
                {/* Header */}
                <div className="glass-card p-6 bg-gradient-to-r from-[#D4A017]/10 to-[#D9A82C]/10 border-orange-500/30">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Surrender & Maturity Benefits
                  </h2>
                  <p className="text-[#3A4557]">What happens if you surrender the policy or it matures</p>
                </div>

                {/* Maturity Benefit */}
                {plan.maturity_benefit && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                      <Award className="h-5 w-5 text-[#A87608]" />
                      Maturity Benefit
                    </h3>
                    {typeof plan.maturity_benefit === 'object' ? (
                      <div className="space-y-3">
                        {plan.maturity_benefit.without_rop && (
                          <div className="bg-[#EDF1F8]/50 rounded-lg p-4">
                            <p className="text-[#3A4557] text-sm mb-1">Without Return of Premium</p>
                            <p className="text-[#1B2A4A] font-semibold">{plan.maturity_benefit.without_rop}</p>
                          </div>
                        )}
                        {plan.maturity_benefit.with_rop && (
                          <div className="bg-[#D4A017]/10 rounded-lg p-4 border border-[#D4A017]/20">
                            <p className="text-[#3A4557] text-sm mb-1">With Return of Premium</p>
                            <p className="text-[#A87608] font-semibold">{plan.maturity_benefit.with_rop}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[#1B2A4A]">{plan.maturity_benefit}</p>
                    )}
                  </div>
                )}

                {/* Surrender Value */}
                {plan.surrender_value && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-orange-600" />
                      Surrender Value
                    </h3>
                    
                    {typeof plan.surrender_value === 'object' ? (
                      <div className="space-y-6">
                        {/* With ROP */}
                        {plan.surrender_value.with_rop && (
                          <div className="bg-orange-500/5 rounded-xl p-5 border border-orange-500/20">
                            <h4 className="font-semibold text-orange-600 mb-3">With Return of Premium Option</h4>
                            <div className="space-y-3">
                              {plan.surrender_value.with_rop.gsv && (
                                <div>
                                  <p className="text-[#3A4557] text-sm">Guaranteed Surrender Value (GSV)</p>
                                  <p className="text-[#1B2A4A]">{plan.surrender_value.with_rop.gsv}</p>
                                </div>
                              )}
                              {plan.surrender_value.with_rop.ssv && (
                                <div>
                                  <p className="text-[#3A4557] text-sm">Special Surrender Value (SSV)</p>
                                  <p className="text-[#1B2A4A]">{plan.surrender_value.with_rop.ssv}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Without ROP */}
                        {plan.surrender_value.without_rop && (
                          <div className="bg-[#EDF1F8]/50 rounded-xl p-5">
                            <h4 className="font-semibold text-[#2E3B57] mb-3">Without Return of Premium Option</h4>
                            <div className="space-y-3">
                              {plan.surrender_value.without_rop.policy_cancellation_value && (
                                <div>
                                  <p className="text-[#3A4557] text-sm">Policy Cancellation Value</p>
                                  <p className="text-[#1B2A4A]">{plan.surrender_value.without_rop.policy_cancellation_value}</p>
                                </div>
                              )}
                              {plan.surrender_value.without_rop.formula && (
                                <div className="bg-[#EDF1F8]/30 rounded-lg p-3 border border-[#C9D4E6] mt-3">
                                  <p className="text-[#3A4557] text-sm">Formula</p>
                                  <p className="text-[#2E3B57] font-mono text-sm">{plan.surrender_value.without_rop.formula}</p>
                                </div>
                              )}
                              {plan.surrender_value.without_rop.pcv_factor && (
                                <div>
                                  <p className="text-[#3A4557] text-sm">PCV Factor</p>
                                  <p className="text-[#1B2A4A]">{plan.surrender_value.without_rop.pcv_factor}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[#1B2A4A]">{plan.surrender_value}</p>
                    )}
                  </div>
                )}

                {/* Tax Benefits */}
                {plan.tax_benefits && (
                  <ExpandableSection
                    title="Tax Benefits"
                    icon={Percent}
                    iconColor="from-green-500 to-emerald-500"
                    defaultOpen={false}
                  >
                    {typeof plan.tax_benefits === 'object' ? (
                      <div className="space-y-3">
                        {plan.tax_benefits.section_80c && (
                          <div className="flex justify-between items-center py-2 border-b border-green-500/10">
                            <span className="text-[#3A4557]">Section 80C (Premium)</span>
                            <span className="text-emerald-600 font-medium">{plan.tax_benefits.section_80c}</span>
                          </div>
                        )}
                        {plan.tax_benefits.section_10_10d && (
                          <div className="flex justify-between items-center py-2 border-b border-green-500/10">
                            <span className="text-[#3A4557]">Section 10(10D) (Claim)</span>
                            <span className="text-emerald-600 font-medium">{plan.tax_benefits.section_10_10d}</span>
                          </div>
                        )}
                        {plan.tax_benefits.note && (
                          <p className="text-[#3A4557] text-sm mt-3">{plan.tax_benefits.note}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-[#1B2A4A]">{plan.tax_benefits}</p>
                    )}
                  </ExpandableSection>
                )}

                {/* Important Note */}
                <div className="glass-card p-4 bg-[#D4A017]/5 border-[#D4A017]/20">
                  <p className="text-sm text-[#3A4557] flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-[#A87608] flex-shrink-0 mt-0.5" />
                    <span>
                      Term insurance plans typically don't have maturity benefits unless you opt for "Return of Premium" variant. 
                      Surrender values are generally low in pure term plans. Please consult your advisor before surrendering.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* BENEFITS */}
            {activeTab === "benefits" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                      <Zap className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Key Benefits & Features
                  </h2>
                  <p className="text-[#3A4557] mb-6">Understand what makes this plan special</p>
                </div>

                {plan.core_benefits && plan.core_benefits.length > 0 ? (
                  plan.core_benefits.map((benefit, idx) => (
                    <ExpandableSection
                      key={idx}
                      title={<>{benefit.name}{benefit.is_new && <span className="ml-2 text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold align-middle">NEW</span>}</>}
                      icon={Zap}
                      iconColor="from-emerald-500 to-green-500"
                      defaultOpen={idx === 0}
                    >
                      <div className="space-y-4">
                        <p className="text-[#2E3B57] text-lg leading-relaxed">{benefit.description}</p>
                        
                        {benefit.details && benefit.details.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-semibold text-emerald-600 mb-3">Details:</h4>
                            <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                              <ul className="space-y-3">
                                {benefit.details.map((d, i) => (
                                  <li key={i} className="flex items-start gap-3 text-[#2E3B57]">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    <span>{d}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </ExpandableSection>
                  ))
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Shield className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Benefit details not available. Please refer to policy documents.</p>
                  </div>
                )}

                {/* Health Checkup Benefits */}
                {plan.health_checkup && (
                  <ExpandableSection
                    title="Annual Health Checkup"
                    icon={Stethoscope}
                    iconColor="from-[#EDF1F8] to-cyan-500"
                    defaultOpen={false}
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 mb-4">
                        <p className="text-[#2E3B57]">
                          <strong className="text-[#1B2A4A]">Availability:</strong> {plan.health_checkup.availability || "Post first policy year"}
                        </p>
                        {plan.health_checkup.frequency && (
                          <span className="px-3 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-sm">
                            {plan.health_checkup.frequency}
                          </span>
                        )}
                      </div>
                      
                      {plan.health_checkup.limits_individual && (
                        <div>
                          <h4 className="font-semibold text-[#2E3B57] mb-3">Individual Policy Limits:</h4>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(plan.health_checkup.limits_individual).map(([si, limit]) => (
                              <div key={si} className="bg-[#EDF1F8]/30 rounded-lg p-3 border border-[#C9D4E6]">
                                <p className="text-[#3A4557] text-sm">Sum Insured: {si}</p>
                                <p className="text-[#1B2A4A] font-bold">{limit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {plan.health_checkup.limits_floater && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-[#2E3B57] mb-3">Floater Policy Limits:</h4>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(plan.health_checkup.limits_floater).map(([si, limit]) => (
                              <div key={si} className="bg-[#EDF1F8]/30 rounded-lg p-3 border border-[#C9D4E6]">
                                <p className="text-[#3A4557] text-sm">Sum Insured: {si}</p>
                                <p className="text-[#1B2A4A] font-bold">{limit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ExpandableSection>
                )}
              </div>
            )}

            {/* COVERAGE */}
            {activeTab === "coverage" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-cyan-500 flex items-center justify-center">
                      <Activity className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Coverage Details
                  </h2>
                  <p className="text-[#3A4557]">Complete breakdown of what's covered under this plan</p>
                </div>

                {plan.coverage_details && Object.keys(plan.coverage_details).length > 0 ? (
                  Object.entries(plan.coverage_details).map(([category, details], idx) => {
                    const getCategoryIcon = (cat) => {
                      const c = cat.toLowerCase();
                      if (c.includes('hospital')) return Building2;
                      if (c.includes('ambulance')) return Ambulance;
                      if (c.includes('ayush')) return Heart;
                      if (c.includes('pre') || c.includes('post')) return Clock;
                      if (c.includes('day')) return Calendar;
                      if (c.includes('organ')) return Heart;
                      if (c.includes('consumable')) return Pill;
                      return Activity;
                    };
                    const Icon = getCategoryIcon(category);
                    
                    return (
                      <ExpandableSection
                        key={category}
                        title={category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        icon={Icon}
                        iconColor="from-[#EDF1F8] to-cyan-500"
                        defaultOpen={idx < 3}
                      >
                        {typeof details === 'object' && details !== null ? (
                          <div className="space-y-3">
                            {Object.entries(details).map(([key, value]) => (
                              <DetailRow 
                                key={key}
                                label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                value={renderValue(value)}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-[#1B2A4A] font-medium">{renderValue(details)}</p>
                        )}
                      </ExpandableSection>
                    );
                  })
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Activity className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Coverage details not available. Please refer to policy documents.</p>
                  </div>
                )}
              </div>
            )}

            {/* WAITING PERIODS */}
            {activeTab === "waiting" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
                      <Clock className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Waiting Periods Explained
                  </h2>
                  <p className="text-[#3A4557]">Time you need to wait before coverage starts for certain conditions</p>
                </div>

                {/* Important Notice */}
                <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-[#D4A017]/15 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-[#A87608]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#A87608] mb-2">Important Information</h3>
                      <p className="text-[#8F6A06]/80 text-sm leading-relaxed">
                        Waiting periods are mandatory periods from the policy start date during which specific types of claims are not covered. 
                        These are standard across the insurance industry and help prevent adverse selection.
                      </p>
                    </div>
                  </div>
                </div>
                
                {plan.waiting_periods && Object.keys(plan.waiting_periods).length > 0 ? (
                  Object.entries(plan.waiting_periods).map(([key, value], idx) => {
                    const getWaitingDescription = (k) => {
                      const descriptions = {
                        initial: "The policy does not cover any hospitalization during this initial period from the date of commencement.",
                        pre_existing: "Pre-existing diseases (PED) are covered only after this waiting period. PED means any condition, ailment, injury or disease that existed before the first policy.",
                        specific_diseases: "Listed specific diseases have their own waiting period. Common examples include hernia, cataracts, joint replacement, etc.",
                        maternity: "Maternity expenses including childbirth, C-section, and related complications are covered after this period."
                      };
                      return descriptions[k] || "Please refer to policy documents for details.";
                    };
                    
                    const parseWaitingValue = (val) => {
                      if (typeof val === 'string') return { period: val };
                      if (typeof val === 'object') return val;
                      return { period: String(val) };
                    };
                    
                    const waitingData = parseWaitingValue(value);
                    
                    return (
                      <ExpandableSection
                        key={key}
                        title={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        icon={Clock}
                        iconColor="from-[#EDF1F8] to-[#DCE4F2]"
                        badge={waitingData.period}
                        defaultOpen={true}
                      >
                        <div className="space-y-4">
                          <p className="text-[#2E3B57] leading-relaxed">{getWaitingDescription(key)}</p>
                          
                          <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <p className="text-[#3A4557] text-sm mb-1">Waiting Period</p>
                                <p className="text-2xl font-bold text-[#1B2A4A]">{waitingData.period}</p>
                              </div>
                              {waitingData.reduction && (
                                <div>
                                  <p className="text-[#3A4557] text-sm mb-1">Reduction Option</p>
                                  <p className="text-emerald-600 font-medium">{waitingData.reduction}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {waitingData.diseases && Array.isArray(waitingData.diseases) && (
                            <div className="mt-4">
                              <h4 className="font-semibold text-[#2E3B57] mb-3">Diseases Covered Under This Waiting:</h4>
                              <div className="grid sm:grid-cols-2 gap-2">
                                {waitingData.diseases.map((disease, i) => (
                                  <div key={i} className="flex items-center gap-2 text-[#2E3B57] text-sm">
                                    <div className="h-2 w-2 rounded-full bg-blue-400" />
                                    {disease}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </ExpandableSection>
                    );
                  })
                ) : plan.waiting_period ? (
                  /* Term Insurance Waiting Period - singular field */
                  <div className="space-y-4">
                    {/* General Waiting Period */}
                    <ExpandableSection
                      title="General Waiting Period"
                      icon={Clock}
                      iconColor="from-[#EDF1F8] to-cyan-500"
                      badge={typeof plan.waiting_period === 'object' ? plan.waiting_period.general : plan.waiting_period}
                      defaultOpen={true}
                    >
                      <div className="space-y-4">
                        <p className="text-[#2E3B57] leading-relaxed">
                          For term insurance, the waiting period is the time from policy commencement during which death claims (other than accidental) are not payable in full.
                        </p>
                        
                        <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <p className="text-[#3A4557] text-sm mb-1">Waiting Period</p>
                              <p className="text-2xl font-bold text-[#1B2A4A]">
                                {typeof plan.waiting_period === 'object' ? plan.waiting_period.general : plan.waiting_period}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {typeof plan.waiting_period === 'object' && plan.waiting_period.exception && (
                          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                            <h4 className="font-semibold text-emerald-600 mb-2 flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4" /> Exception
                            </h4>
                            <p className="text-[#2E3B57]">{plan.waiting_period.exception}</p>
                          </div>
                        )}
                      </div>
                    </ExpandableSection>

                    {/* Suicide Clause - Important for Term Insurance */}
                    {plan.suicide_clause && (
                      <ExpandableSection
                        title="Suicide Clause"
                        icon={AlertTriangle}
                        iconColor="from-[#D4A017] to-[#D9A82C]"
                        badge={typeof plan.suicide_clause === 'object' ? plan.suicide_clause.period : plan.suicide_clause}
                        defaultOpen={true}
                      >
                        <div className="space-y-4">
                          <p className="text-[#2E3B57] leading-relaxed">
                            As per IRDAI regulations, claims arising from suicide within a specified period from policy commencement have limited benefits.
                          </p>
                          
                          <div className="bg-[#D4A017]/10 rounded-xl p-4 border border-[#D4A017]/20">
                            <div className="grid sm:grid-cols-1 gap-4">
                              <div>
                                <p className="text-[#3A4557] text-sm mb-1">Suicide Exclusion Period</p>
                                <p className="text-2xl font-bold text-[#1B2A4A]">
                                  {typeof plan.suicide_clause === 'object' ? plan.suicide_clause.period : plan.suicide_clause}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {typeof plan.suicide_clause === 'object' && plan.suicide_clause.benefit && (
                            <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                              <h4 className="font-semibold text-[#2E3B57] mb-2">Benefit Payable (if within exclusion period)</h4>
                              <p className="text-[#2E3B57]">{plan.suicide_clause.benefit}</p>
                            </div>
                          )}
                        </div>
                      </ExpandableSection>
                    )}

                    {/* Free Look Period */}
                    {plan.free_look_period && (
                      <ExpandableSection
                        title="Free Look Period"
                        icon={Eye}
                        iconColor="from-green-500 to-emerald-500"
                        badge={typeof plan.free_look_period === 'object' ? plan.free_look_period.duration : plan.free_look_period}
                        defaultOpen={false}
                      >
                        <div className="space-y-4">
                          <p className="text-[#2E3B57] leading-relaxed">
                            You can return the policy within this period if you're not satisfied, and get a refund of premium (minus certain charges).
                          </p>
                          
                          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                            <div>
                              <p className="text-[#3A4557] text-sm mb-1">Free Look Period</p>
                              <p className="text-2xl font-bold text-emerald-600">
                                {typeof plan.free_look_period === 'object' ? plan.free_look_period.duration : plan.free_look_period}
                              </p>
                            </div>
                          </div>
                          
                          {typeof plan.free_look_period === 'object' && plan.free_look_period.details && (
                            <p className="text-[#3A4557] text-sm">{plan.free_look_period.details}</p>
                          )}
                        </div>
                      </ExpandableSection>
                    )}
                  </div>
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Clock className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Waiting period details not available.</p>
                  </div>
                )}

                {/* Policy Document Reference */}
                {plan.documents?.find(d => d.type === 'waiting_period') && (
                  <div className="glass-card p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-[#2E3B57]" />
                        <span className="text-[#1B2A4A] font-medium">View Complete Waiting Period List</span>
                      </div>
                      <a 
                        href={plan.documents.find(d => d.type === 'waiting_period').url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline px-4 py-2 text-sm"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* EXCLUSIONS */}
            {activeTab === "exclusions" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center">
                      <XCircle className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    What's NOT Covered (Exclusions)
                  </h2>
                  <p className="text-[#3A4557]">Claims for these will not be paid under this policy</p>
                </div>

                {/* Warning Notice */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-red-600 mb-2">Please Read Carefully</h3>
                      <p className="text-red-800/80 text-sm leading-relaxed">
                        Exclusions are conditions, treatments, or circumstances that are not covered by the policy. 
                        Claims related to exclusions will be rejected. Please read the policy document for complete exclusion list.
                      </p>
                    </div>
                  </div>
                </div>
                
                {plan.exclusions && plan.exclusions.length > 0 ? (
                  <div className="glass-card p-6">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {plan.exclusions.map((exc, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-4 bg-red-500/5 rounded-xl border border-red-500/10">
                          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <span className="text-[#2E3B57]">{exc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glass-card p-8 text-center">
                    <XCircle className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Exclusion list not available. Please refer to policy documents.</p>
                  </div>
                )}
              </div>
            )}

            {/* RIDERS - Detailed with Policy Wordings */}
            {activeTab === "riders" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center">
                      <Heart className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Add-on Covers (Riders)
                  </h2>
                  <p className="text-[#3A4557]">Optional benefits for extra protection at additional premium</p>
                </div>

                {/* What are Riders */}
                <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                      <Info className="h-5 w-5 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-pink-600 mb-2">What are Riders?</h3>
                      <p className="text-pink-800/80 text-sm leading-relaxed">
                        Riders are optional add-on benefits that can be purchased along with your base health insurance policy. 
                        They enhance your coverage for specific needs like maternity, critical illness, OPD expenses, etc. 
                        Each rider has its own premium and terms.
                      </p>
                    </div>
                  </div>
                </div>
                
                {plan.riders && plan.riders.length > 0 ? (
                  plan.riders.map((rider, idx) => {
                    const RiderIcon = getRiderIcon(rider.name);
                    const riderDoc = findRiderDocument(rider.id, rider.name);
                    const isExpanded = expandedRiders[rider.id || idx];
                    
                    return (
                      <div key={rider.id || idx} className="glass-card overflow-hidden">
                        {/* Rider Header - Always Visible */}
                        <button
                          onClick={() => toggleRiderExpand(rider.id || idx)}
                          className="w-full flex items-center justify-between p-5 hover:bg-[#EDF1F8]/20 transition-colors"
                          data-testid={`rider-${rider.id || idx}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center flex-shrink-0">
                              <RiderIcon className="h-7 w-7 text-[#1B2A4A]" />
                            </div>
                            <div className="text-left">
                              <h3 className="font-bold text-[#1B2A4A] text-xl">{rider.name}</h3>
                              <p className="text-[#3A4557] text-sm mt-1">{rider.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {rider.cost && (
                              <span className="px-3 py-1 bg-pink-500/20 text-pink-600 rounded-full text-sm font-medium">
                                {rider.cost}
                              </span>
                            )}
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                            )}
                          </div>
                        </button>
                        
                        {/* Rider Details - Expandable */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="px-5 pb-5 border-t border-[#C9D4E6] pt-4 space-y-4">
                                {/* Details */}
                                {rider.details && rider.details.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-pink-700 mb-3 flex items-center gap-2">
                                      <CheckCircle2 className="h-4 w-4" />
                                      Key Features
                                    </h4>
                                    <div className="bg-pink-500/5 rounded-xl p-4 border border-pink-500/10">
                                      <ul className="space-y-3">
                                        {rider.details.map((d, i) => (
                                          <li key={i} className="flex items-start gap-3 text-[#2E3B57]">
                                            <CheckCircle2 className="h-5 w-5 text-pink-600 flex-shrink-0 mt-0.5" />
                                            <span>{d}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                )}

                                {/* Additional Info Grid */}
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {rider.waiting_period && (
                                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                                      <p className="text-[#3A4557] text-sm mb-1 flex items-center gap-2">
                                        <Clock className="h-4 w-4" />
                                        Waiting Period
                                      </p>
                                      <p className="text-[#1B2A4A] font-bold">{rider.waiting_period}</p>
                                    </div>
                                  )}
                                  {rider.sum_insured && (
                                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                                      <p className="text-[#3A4557] text-sm mb-1 flex items-center gap-2">
                                        <IndianRupee className="h-4 w-4" />
                                        Sum Insured
                                      </p>
                                      <p className="text-[#1B2A4A] font-bold">{rider.sum_insured}</p>
                                    </div>
                                  )}
                                  {rider.eligibility && (
                                    <div className="bg-[#EDF1F8]/30 rounded-xl p-4 border border-[#C9D4E6]">
                                      <p className="text-[#3A4557] text-sm mb-1 flex items-center gap-2">
                                        <Users className="h-4 w-4" />
                                        Eligibility
                                      </p>
                                      <p className="text-[#1B2A4A] font-bold">{rider.eligibility}</p>
                                    </div>
                                  )}
                                </div>

                                {/* Policy Wordings Document */}
                                {riderDoc && (
                                  <div className="bg-gradient-to-r from-pink-500/10 to-[#DCE4F2]/10 rounded-xl p-4 border border-pink-500/20">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <BookOpen className="h-5 w-5 text-pink-600" />
                                        <div>
                                          <p className="text-[#1B2A4A] font-medium">Policy Wordings Document</p>
                                          <p className="text-[#3A4557] text-sm">Official terms and conditions</p>
                                        </div>
                                      </div>
                                      <a 
                                        href={riderDoc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg transition-colors font-medium"
                                      >
                                        <Download className="h-4 w-4" />
                                        Download PDF
                                      </a>
                                    </div>
                                  </div>
                                )}

                                {/* Call to Action */}
                                <div className="flex items-center justify-between pt-2 border-t border-blue-500/10">
                                  <p className="text-[#3A4557] text-sm">
                                    Want to add this rider to your policy?
                                  </p>
                                  <a href="tel:+919510020907" className="text-[#A87608] font-medium flex items-center gap-2 hover:text-[#A87608]">
                                    <Phone className="h-4 w-4" />
                                    Get Quote
                                  </a>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Heart className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557] font-medium">No riders available for this plan</p>
                    <p className="text-[#3A4557] text-sm mt-2">This plan does not offer additional rider options</p>
                  </div>
                )}

                {/* All Rider Documents */}
                {plan.rider_documents && plan.rider_documents.length > 0 && (
                  <div className="glass-card p-6">
                    <h3 className="font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-pink-600" />
                      All Rider Documents
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {plan.rider_documents.map((doc, idx) => (
                        <a 
                          key={idx}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/20 rounded-xl transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-pink-600" />
                            <span className="text-[#1B2A4A] font-medium">{doc.name}</span>
                          </div>
                          <Download className="h-4 w-4 text-[#3A4557]" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DISCOUNTS */}
            {activeTab === "discounts" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                      <Percent className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Available Discounts
                  </h2>
                  <p className="text-[#3A4557]">Save money on your premium with these discounts</p>
                </div>

                {plan.discounts && plan.discounts.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {plan.discounts.map((discount, idx) => (
                      <div key={idx} className="glass-card p-5">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                            <Percent className="h-6 w-6 text-[#1B2A4A]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-bold text-[#1B2A4A]">{discount.name}</h3>
                              <span className="px-3 py-1 bg-green-500/20 text-green-600 rounded-full text-lg font-bold">
                                {discount.discount}
                              </span>
                            </div>
                            <p className="text-[#3A4557]">{discount.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="glass-card p-8 text-center">
                    <Percent className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">Discount information not available.</p>
                  </div>
                )}
              </div>
            )}

            {/* NETWORK */}
            {activeTab === "network" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-[#DCE4F2] flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Hospital Network
                  </h2>
                  <p className="text-[#3A4557]">Find cashless hospitals and check excluded facilities</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  {plan.hospital_network?.cashless_link && (
                    <a 
                      href={plan.hospital_network.cashless_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="glass-card p-6 hover:bg-emerald-500/10 transition-colors group"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="h-14 w-14 rounded-xl bg-emerald-500 flex items-center justify-center">
                          <Building2 className="h-7 w-7 text-[#1B2A4A]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#1B2A4A] text-lg">Cashless Hospitals</h3>
                          <p className="text-[#3A4557]">Find network hospitals near you</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-emerald-600">
                        <span className="font-medium">Search Hospital Network</span>
                        <ExternalLink className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </a>
                  )}
                  
                  {plan.hospital_network?.excluded_link && (
                    <a 
                      href={plan.hospital_network.excluded_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="glass-card p-6 hover:bg-red-500/10 transition-colors group"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="h-14 w-14 rounded-xl bg-red-500 flex items-center justify-center">
                          <Ban className="h-7 w-7 text-[#1B2A4A]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#1B2A4A] text-lg">Excluded Hospitals</h3>
                          <p className="text-[#3A4557]">Hospitals not covered for cashless</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-red-600">
                        <span className="font-medium">View Exclusion List</span>
                        <ExternalLink className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </a>
                  )}
                </div>

                {/* Important Note */}
                <div className="bg-[#EDF1F8]/30 border border-blue-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-xl bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                      <Info className="h-5 w-5 text-[#2E3B57]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2E3B57] mb-2">Important</h3>
                      <p className="text-blue-800/80 text-sm leading-relaxed">
                        Always verify the hospital's network status before admission. Hospital networks may change without notice. 
                        For planned hospitalization, get a pre-authorization to ensure cashless facility.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DOCUMENTS */}
            {activeTab === "documents" && (
              <div className="space-y-4">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-[#1B2A4A] mb-2 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gray-500 to-slate-500 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    Policy Documents
                  </h2>
                  <p className="text-[#3A4557]">Download official policy documents and brochures</p>
                </div>
                
                {plan.documents && plan.documents.length > 0 ? (
                  <>
                    {/* Group by type */}
                    {['policy_wording', 'brochure', 'summary', 'cis', 'waiting_period', 'health_checkup'].map(type => {
                      const docs = plan.documents.filter(d => d.type === type);
                      if (docs.length === 0) return null;
                      
                      const typeLabels = {
                        policy_wording: { label: "Policy Wordings", desc: "Complete terms and conditions", color: "purple" },
                        brochure: { label: "Brochures", desc: "Product overview and features", color: "blue" },
                        summary: { label: "Summary Documents", desc: "Quick reference guides", color: "green" },
                        cis: { label: "Customer Information Sheet", desc: "Key product information", color: "amber" },
                        waiting_period: { label: "Waiting Period Details", desc: "Disease-wise waiting periods", color: "orange" },
                        health_checkup: { label: "Health Checkup Benefits", desc: "Annual checkup limits", color: "cyan" }
                      };
                      
                      const { label, desc, color } = typeLabels[type] || { label: type, desc: "", color: "gray" };
                      
                      return (
                        <div key={type} className="glass-card p-5">
                          <h3 className="font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                            <FileText className={`h-5 w-5 text-${color}-700`} />
                            {label}
                            <span className="text-[#3A4557] font-normal text-sm">- {desc}</span>
                          </h3>
                          <div className="grid sm:grid-cols-2 gap-3">
                            {docs.map((doc, idx) => (
                              <a 
                                key={idx} 
                                href={doc.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={() => trackDocumentDownload(doc.name, doc.type, plan.name)}
                                className={`flex items-center justify-between p-4 bg-${color}-500/10 hover:bg-${color}-500/20 border border-${color}-500/20 rounded-xl transition-colors`}
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className={`h-5 w-5 text-${color}-700`} />
                                  <span className="text-[#1B2A4A] font-medium">{doc.name}</span>
                                  {doc.is_new && <span className="text-[10px] bg-emerald-500 text-white px-1.5 py-0.5 rounded-full font-bold">NEW</span>}
                                </div>
                                <Download className="h-4 w-4 text-[#3A4557]" />
                              </a>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Rider Documents Section */}
                    {plan.rider_documents && plan.rider_documents.length > 0 && (
                      <div className="glass-card p-5">
                        <h3 className="font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                          <Heart className="h-5 w-5 text-pink-600" />
                          Rider Policy Wordings
                          <span className="text-[#3A4557] font-normal text-sm">- Add-on cover terms</span>
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {plan.rider_documents.map((doc, idx) => (
                            <a 
                              key={idx} 
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={() => trackDocumentDownload(doc.name, 'rider', plan.name)}
                              className="flex items-center justify-between p-4 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/20 rounded-xl transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-pink-600" />
                                <span className="text-[#1B2A4A] font-medium">{doc.name}</span>
                              </div>
                              <Download className="h-4 w-4 text-[#3A4557]" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Important Links Section */}
                    {plan.important_links && plan.important_links.length > 0 && (
                      <div className="glass-card p-5">
                        <h3 className="font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                          <Link2 className="h-5 w-5 text-[#A87608]" />
                          Important Links
                          <span className="text-[#3A4557] font-normal text-sm">- Quick access to useful resources</span>
                        </h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {plan.important_links.map((link, idx) => {
                            const getIcon = (iconName) => {
                              const icons = {
                                'building': Building2,
                                'alert-triangle': AlertTriangle,
                                'calculator': Calculator,
                                'refresh-cw': RefreshCw,
                                'headphones': Headphones,
                                'file-text': FileText,
                                'upload': Upload,
                                'smartphone': Smartphone
                              };
                              return icons[iconName] || Link2;
                            };
                            const IconComponent = getIcon(link.icon);
                            
                            return (
                              <a 
                                key={idx} 
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex flex-col p-4 bg-[#D4A017]/10 hover:bg-[#D4A017]/15 border border-[#D4A017]/20 rounded-xl transition-colors group"
                              >
                                <div className="flex items-center gap-3 mb-2">
                                  <IconComponent className="h-5 w-5 text-[#A87608]" />
                                  <span className="text-[#1B2A4A] font-medium">{link.name}</span>
                                  <ExternalLink className="h-3 w-3 text-[#3A4557] ml-auto group-hover:text-[#A87608] transition-colors" />
                                </div>
                                <p className="text-[#3A4557] text-sm">{link.description}</p>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="glass-card p-8 text-center">
                    <FileText className="h-12 w-12 text-[#3A4557] mx-auto mb-4" />
                    <p className="text-[#3A4557]">No documents available.</p>
                  </div>
                )}
                
                {/* Library Documents Section - Documents uploaded via Document Library */}
                {libraryDocuments && libraryDocuments.length > 0 && (
                  <div className="glass-card p-5 mt-4">
                    <h3 className="font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-orange-600" />
                      Uploaded Documents
                      <span className="text-[#3A4557] font-normal text-sm">- From Document Library</span>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {libraryDocuments.map((doc, idx) => (
                        <a 
                          key={idx} 
                          href={`${API}/plans/documents/${doc.id}/download`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={() => trackDocumentDownload(doc.original_filename, doc.document_type, plan?.name)}
                          className="flex items-center justify-between p-4 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 rounded-xl transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-orange-600 flex-shrink-0" />
                              <span className="text-[#1B2A4A] font-medium truncate">{doc.original_filename}</span>
                            </div>
                            <p className="text-xs text-[#3A4557] ml-8 mt-1">{doc.document_type}</p>
                          </div>
                          <Download className="h-4 w-4 text-[#3A4557] flex-shrink-0 ml-2" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ASK AI */}
            {activeTab === "askme" && (
              <div className="max-w-3xl mx-auto glass-card overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-[#DCE4F2] p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-white/20 flex items-center justify-center">
                      <Sparkles className="h-7 w-7 text-[#1B2A4A]" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-[#1B2A4A]">Ask AI About {plan.name}</h2>
                      <p className="text-[#1B2A4A]/80">Get instant answers based on policy documents</p>
                    </div>
                  </div>
                </div>

                <div className="h-96 overflow-y-auto p-6 space-y-4 bg-blue-900/20">
                  {askHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <Bot className="h-16 w-16 text-[#3A4557] mx-auto mb-4" />
                      <p className="text-[#3A4557] mb-4">Ask me anything about this plan!</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {[
                          "What is the PED waiting period?",
                          "What riders are available?",
                          "Is maternity covered?",
                          "What's the room rent limit?"
                        ].map((q, i) => (
                          <button
                            key={i}
                            onClick={() => handleAskQuestion(q)}
                            className="px-4 py-2 bg-[#EDF1F8]/40 hover:bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-sm transition-colors"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    askHistory.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                          msg.type === 'user' ? 'bg-gradient-to-r from-[#EDF1F8] to-[#DCE4F2] text-[#1B2A4A]' 
                          : msg.type === 'error' ? 'bg-red-500/20 text-red-700 border border-red-500/30'
                          : 'bg-[#EDF1F8]/40 text-[#2E3B57] border border-[#C9D4E6]'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                        </div>
                      </div>
                    ))
                  )}
                  {askLoading && (
                    <div className="flex justify-start">
                      <div className="bg-[#EDF1F8]/40 rounded-2xl px-5 py-3 border border-[#C9D4E6]">
                        <Loader2 className="h-5 w-5 animate-spin text-[#2E3B57]" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                <div className="p-4 border-t border-[#C9D4E6]">
                  <form onSubmit={(e) => { e.preventDefault(); handleAskQuestion(); }} className="flex gap-3">
                    <Input
                      value={askQuestion}
                      onChange={(e) => setAskQuestion(e.target.value)}
                      placeholder="Type your question..."
                      className="input-dark flex-1 h-12"
                      disabled={askLoading}
                      data-testid="ask-input"
                    />
                    <Button type="submit" disabled={askLoading || !askQuestion.trim()} className="btn-blue h-12 px-6" data-testid="ask-submit">
                      <Send className="h-5 w-5" />
                    </Button>
                  </form>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer CTA */}
      <section className="mt-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto glass-card p-8 text-center">
          <h2 className="text-2xl font-bold text-[#1B2A4A] mb-3">Interested in {plan.name}?</h2>
          <p className="text-[#3A4557] mb-6">Get personalized advice from our expert insurance advisor</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919510020907">
              <Button className="btn-gold h-12 px-8">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
            <a href="https://wa.me/919510020907" target="_blank" rel="noopener noreferrer">
              <Button className="btn-outline h-12 px-8">WhatsApp</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
